import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import "./CandidateProfile.css";
import { resolveLogoSrc } from "../../../../utils/logoUtils";
import SavedJob from "./SavedJob/SavedJob"



const API = "/api";
const API_ORIGIN = (() => {
  const raw = process.env.REACT_APP_API_URL || "http://localhost:3000";
  return raw.replace(/\/api$/, "").replace(/\/$/, "");
})();

/* ─── AUTH HELPERS ─────────────────────────────────────── */
const getToken = () => localStorage.getItem("th_token") || localStorage.getItem("token") || "";
const getUser = () => {
  try {
    const r = localStorage.getItem("th_user") || localStorage.getItem("user");
    return r ? JSON.parse(r) : {};
  } catch { return {}; }
};
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

function persistUser(updated) {
  try {
    localStorage.setItem("th_user", JSON.stringify(updated));
    localStorage.setItem("user", JSON.stringify(updated));
    window.dispatchEvent(new Event("auth:update"));
  } catch {}
}

function resolvePhoto(raw) {
  if (!raw) return null;
  if (raw.startsWith("http") || raw.startsWith("blob:")) return raw;
  return `${raw}`;
}

function resolveApiFileUrl(raw) {
  if (!raw) return "";
  if (raw.startsWith("http") || raw.startsWith("blob:")) return raw;
  return `${API_ORIGIN}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function tryParse(v, fb = []) {
  try { return typeof v === "string" ? JSON.parse(v) : (v || fb); } catch { return fb; }
}

function getPublicProfileUrl(username) {
  if (!username || typeof window === "undefined") return "";
  return `${window.location.origin}/candidate/${username}`;
}

/* ─── PROFANITY CHECK ─────────────────────────────────── */
const PROFANITY = ["fuck","shit","ass","bitch","damn","cunt","dick","cock","pussy","bastard","asshole","motherfucker","whore","slut","nigger","faggot","retard"];
function hasProfanity(text) {
  if (!text) return false;
  const lower = text.toLowerCase().replace(/[^a-z\s]/g, " ");
  return PROFANITY.some(w => new RegExp(`\\b${w}\\b`).test(lower));
}

/* ─── VALIDATORS ──────────────────────────────────────── */
const VALIDATORS = {
  first_name: v => { if (!v?.trim()) return "First name is required"; if (v.trim().length < 2) return "Min 2 characters"; if (!/^[a-zA-Z\s'-]+$/.test(v.trim())) return "Letters only"; return null; },
  last_name:  v => { if (!v?.trim()) return "Last name is required"; if (!/^[a-zA-Z\s'-]+$/.test(v.trim())) return "Letters only"; return null; },
  email:      v => { if (!v?.trim()) return "Email is required"; if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Invalid email"; return null; },
  mobile:     v => { if (!v?.trim()) return "Mobile is required"; const d = v.replace(/\D/g,""); if (d.length < 10) return "Min 10 digits"; return null; },
  aadhar:     v => { if (!v?.trim()) return "Aadhaar required"; const d = v.replace(/\D/g,""); if (d.length !== 12) return `Must be 12 digits (you entered ${d.length})`; return null; },
  pan:        v => { if (!v?.trim()) return "PAN required"; if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v.trim().toUpperCase())) return "Format: ABCDE1234F"; return null; },
  headline:   v => { if (!v?.trim()) return "Headline required"; if (v.trim().length < 10) return "Min 10 characters"; if (v.trim().length > 200) return "Max 200 characters"; return null; },
  salary:     v => { if (!v?.trim()) return null; if (!/\d/.test(v)) return "Enter a valid amount"; return null; },
  degree:     v => { if (!v?.trim()) return "Degree is required"; return null; },
  start_year: v => { if (!v?.trim()) return null; const y = parseInt(v); if (isNaN(y) || y < 1950 || y > 2030) return "Year between 1950–2030"; return null; },
  passing_year: v => { if (!v?.trim()) return null; const y = parseInt(v); if (isNaN(y) || y < 1950 || y > 2030) return "Year between 1950–2030"; return null; },
};
function validate(k, v) { return VALIDATORS[k] ? VALIDATORS[k](v) : null; }

/* ─── REWARDS / XP ────────────────────────────────────── */
// Points per the TalentHive Rewards Program document
const REWARD_ACTIONS = {
  phone_verified:    { label: "Phone number verified",     points: 10,  category: "basic",    instant: true  },
  email_verified:    { label: "Email address verified",    points: 10,  category: "basic",    instant: true  },
  photo_uploaded:    { label: "Profile photo uploaded",    points: 10,  category: "basic",    instant: true  },
  aadhar_uploaded:   { label: "Aadhaar card verified",     points: 30,  category: "kyc",      instant: false },
  pan_uploaded:      { label: "PAN card verified",         points: 30,  category: "kyc",      instant: false },
  passport_uploaded: { label: "Passport verified",         points: 60,  category: "kyc",      instant: false },
  cv_uploaded:       { label: "CV / Resume uploaded",      points: 20,  category: "career",   instant: true  },
  resume_built:      { label: "Resume builder used",       points: 15,  category: "career",   instant: true  },
  quiz_completed:    { label: "Quiz completed (pass)",     points: 20,  category: "skill",    instant: true  },
  cert_claimed:      { label: "Certificate claimed",       points: 10,  category: "skill",    instant: true  },
  interview_attended:{ label: "Interview attended",        points: 30,  category: "hiring",   instant: false },
  offer_talenthive:  { label: "Offer letter (TalentHive)", points: 50,  category: "hiring",   instant: false },
  offer_external:    { label: "Offer letter (external)",   points: 25,  category: "hiring",   instant: false },
  referral_signup:   { label: "Referral signed up",        points: 100, category: "referral", instant: true  },
  referral_profile:  { label: "Referred friend profile done",points: 50,category: "referral", instant: false },
  career_talk_first: { label: "First career talk attended",points: 30,  category: "events",   instant: true  },
  career_talk_extra: { label: "Additional career talk",    points: 10,  category: "events",   instant: true  },
  profile_100:       { label: "Profile 100% complete",     points: 20,  category: "bonus",    instant: true  },
  streak_7:          { label: "7-day login streak",        points: 15,  category: "bonus",    instant: true  },
  streak_30:         { label: "30-day login streak",       points: 50,  category: "bonus",    instant: true  },
};

const MILESTONES = [
  { pts: 50,  label: "Starter",   reward: "Welcome gift / ₹50 credit",       badge: "🎁" },
  { pts: 100, label: "Explorer",  reward: "₹100 wallet transfer (UPI/Paytm)", badge: "🧭" },
  { pts: 250, label: "Achiever",  reward: "₹150 credit + Premium Career Talks",badge: "⭐" },
  { pts: 400, label: "Performer", reward: "₹300 credit or gift catalogue",    badge: "🔥" },
  { pts: 500, label: "Champion",  reward: "₹500 full redemption — cash/gift", badge: "👑" },
];

function getLevelInfo(pts) {
  if (pts >= 500) return { title: "Champion",  emoji: "👑", next: 500  };
  if (pts >= 400) return { title: "Performer", emoji: "🔥", next: 500  };
  if (pts >= 250) return { title: "Achiever",  emoji: "⭐", next: 400  };
  if (pts >= 100) return { title: "Explorer",  emoji: "🧭", next: 250  };
  if (pts >= 50)  return { title: "Starter",   emoji: "🎁", next: 100  };
  return                  { title: "Rookie",    emoji: "🚀", next: 50   };
}

/* ─── COMPLETION / XP (profile progress) ─────────────── */
function getCompletion(p) {
  const items = [
    { label:"First Name",    done:!!p?.first_name,           xp:80,  required:true,  key:"first_name"   },
    { label:"Last Name",     done:!!p?.last_name,            xp:40,  required:true,  key:"last_name"    },
    { label:"Mobile",        done:!!p?.mobile,               xp:100, required:true,  key:"mobile"       },
    { label:"Headline",      done:!!p?.headline,             xp:80,  required:true,  key:"headline"     },
    { label:"Gender",        done:!!p?.gender,               xp:40,  required:true,  key:"gender"       },
    { label:"Work Status",   done:!!p?.work_status,          xp:60,  required:false, key:"work_status"  },
    { label:"Education",     done:!!p?.education?.[0]?.degree,xp:200,required:true,  key:"education"    },
    { label:"3+ Skills",     done:(p?.skills?.length||0)>=3, xp:150, required:true,  key:"skills"       },
    { label:"Location",      done:(p?.locations?.length||0)>0,xp:80, required:false, key:"locations"    },
    { label:"Salary",        done:!!p?.salary,               xp:40,  required:false, key:"salary"       },
    { label:"Resume",        done:!!p?.resume_url,           xp:150, required:true,  key:"resume_url"   },
    { label:"Projects",      done:(p?.projects?.length||0)>0,xp:120, required:false, key:"projects"     },
    { label:"Courses",       done:(p?.courses?.length||0)>0, xp:60,  required:false, key:"courses"      },
    { label:"Aadhaar No.",   done:!!p?.aadhar,               xp:80,  required:true,  key:"aadhar"       },
    { label:"PAN No.",       done:!!p?.pan,                  xp:80,  required:true,  key:"pan"          },
    { label:"Country",       done:!!p?.country_iso,          xp:40,  required:true,  key:"country_iso"  },
    { label:"Aadhaar Photo", done:!!p?.aadhar_photo,         xp:50,  required:true,  key:"aadhar_photo" },
    { label:"PAN Photo",     done:!!p?.pan_photo,            xp:50,  required:true,  key:"pan_photo"    },
  ];
  const done         = items.filter(i=>i.done).length;
  const reqDone      = items.filter(i=>i.required&&i.done).length;
  const reqTotal     = items.filter(i=>i.required).length;
  return { items, done, pct:Math.round((done/items.length)*100), reqDone, reqTotal, canApply:reqDone===reqTotal };
}

/* ─── RING ────────────────────────────────────────────── */
function Ring({ pct, size=88, stroke=7 }) {
  const r = (size-stroke)/2;
  const circ = 2*Math.PI*r;
  const dash = (pct/100)*circ;
  return (
    <svg width={size} height={size} style={{ transform:"rotate(-90deg)", display:"block" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--cp-ring-track)" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--cp-brand)" strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={`${dash} ${circ}`}
        style={{ transition:"stroke-dasharray 1.2s ease" }}/>
    </svg>
  );
}

/* ─── TOAST ───────────────────────────────────────────── */
function Toast({ msg, type="success", onDone }) {
  useEffect(()=>{ const t=setTimeout(onDone,3500); return ()=>clearTimeout(t); },[onDone]);
  return (
    <div className={`cp-toast cp-toast--${type}`}>
      <span className="cp-toast__icon">{type==="error"?"✕":"✓"}</span>
      <span>{msg}</span>
    </div>
  );
}

/* ─── FIELD STATUS ────────────────────────────────────── */
function FieldStatus({ status, message, correction, onApply }) {
  if (!status) return null;
  if (status==="saving")  return <div className="cp-fstatus cp-fstatus--saving"><span className="cp-spin cp-spin--xs"/>Saving…</div>;
  if (status==="saved")   return <div className="cp-fstatus cp-fstatus--saved">✓ Saved</div>;
  if (status==="error")   return <div className="cp-fstatus cp-fstatus--error">✕ {message}</div>;
  if (status==="warn")    return <div className="cp-fstatus cp-fstatus--warn">⚠ {message}</div>;
  if (status==="grammar" && correction) return (
    <div className="cp-fstatus cp-fstatus--grammar">
      ✏ Suggestion: <em>"{correction}"</em>
      <button onClick={()=>onApply(correction)} className="cp-grammar-apply">Apply</button>
    </div>
  );
  return null;
}

/* ─── SMART FIELD ─────────────────────────────────────── */
function SmartField({ label, fieldKey, value, placeholder, multiline=false, required=false, masked=false, note, onSave, inputType="text" }) {
  const [editing, setEditing]   = useState(false);
  const [val, setVal]           = useState(value||"");
  const [status, setStatus]     = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const ref = useRef();

  useEffect(()=>setVal(value||""),[value]);
  useEffect(()=>{ if(editing) ref.current?.focus(); },[editing]);
  useEffect(()=>{ if(status==="saved"){ const t=setTimeout(()=>setStatus(null),3000); return ()=>clearTimeout(t); } },[status]);

  const displayValue = () => {
    if (!value) return null;
    if (!masked) return value;
    if (fieldKey==="aadhar" && /^\d{12}$/.test(value)) return `●●●● ●●●● ${value.slice(8)}`;
    if (fieldKey==="pan" && value.length>=5) return `●●●●● ${value.slice(5)}`;
    return value.slice(0,-4).replace(/./g,"●")+value.slice(-4);
  };

  async function commit() {
    setEditing(false);
    const clean = val.trim();
    if (clean===(value||"").trim()) return;
    const err = validate(fieldKey, clean);
    if (err) { setStatus("error"); setStatusMsg(err); setVal(value||""); return; }
    if (hasProfanity(clean)) { setStatus("error"); setStatusMsg("Please use professional language"); setVal(value||""); return; }
    setStatus("saving");
    try {
      await onSave(clean);
      setStatus("saved");
    } catch(e) {
      setStatus("error"); setStatusMsg(e?.response?.data?.error||e?.message||"Save failed");
    }
  }

  const shown = displayValue();

  return (
    <div className="cp-field">
      {label && (
        <div className="cp-field__header">
          <label className="cp-label">{label}{required && <span className="cp-req-star">*</span>}</label>
          <div className="cp-field__actions">
            {required && !value && <span className="cp-req-chip">Required</span>}
          </div>
        </div>
      )}
      <div className="cp-field__input-wrap">
        {editing
          ? multiline
            ? <textarea ref={ref} value={val} className="cp-input cp-input--area"
                onChange={e=>setVal(e.target.value)} onBlur={()=>setTimeout(commit,150)}
                rows={3} placeholder={placeholder}/>
            : <input ref={ref} type={inputType==="year"||inputType==="tel"?"tel":inputType==="email"?"email":"text"}
                value={val} className="cp-input"
                onChange={e=>{ let v=e.target.value; if(inputType==="year") v=v.replace(/\D/g,"").slice(0,4); setVal(v); setStatus(null); }}
                onBlur={()=>setTimeout(commit,150)}
                onKeyDown={e=>{ if(e.key==="Enter") commit(); if(e.key==="Escape"){ setEditing(false); setVal(value||""); setStatus(null); } }}
                placeholder={placeholder} maxLength={inputType==="year"?4:undefined}/>
          : <div className={`cp-display${!value&&required?" cp-display--empty":""}${status==="error"?" cp-display--err":""}`}
              onClick={()=>setEditing(true)}>
              <span className={`cp-display__val${!value?" cp-display__val--placeholder":""}`}>
                {shown||placeholder||"Click to add…"}
              </span>
              <span className="cp-edit-icon">✎</span>
            </div>
        }
      </div>
      <FieldStatus status={status} message={statusMsg}/>
      {note && <p className="cp-field__note">{note}</p>}
    </div>
  );
}

/* ─── SMART SELECT ────────────────────────────────────── */
function SmartSelect({ label, fieldKey, value, options, placeholder, required=false, onSave }) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus]   = useState(null);
  const [msg, setMsg]         = useState("");
  useEffect(()=>{ if(status==="saved"){ const t=setTimeout(()=>setStatus(null),3000); return ()=>clearTimeout(t); } },[status]);

  async function onChange(v) {
    setEditing(false);
    if (v===value) return;
    setStatus("saving");
    try { await onSave(v); setStatus("saved"); }
    catch(e){ setStatus("error"); setMsg(e?.response?.data?.error||"Save failed"); }
  }
  const sel = options.find(o=>(o.value||o)===value);
  const label_ = sel?.label||sel||value;

  return (
    <div className="cp-field">
      {label && (
        <div className="cp-field__header">
          <label className="cp-label">{label}{required&&<span className="cp-req-star">*</span>}</label>
          {required&&!value&&<span className="cp-req-chip">Required</span>}
        </div>
      )}
      {editing
        ? <select className="cp-input" value={value||""} autoFocus
            onChange={e=>onChange(e.target.value)} onBlur={()=>setEditing(false)}>
            <option value="">{placeholder}</option>
            {options.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
          </select>
        : <div className={`cp-display${!value&&required?" cp-display--empty":""}`} onClick={()=>setEditing(true)}>
            <span className={`cp-display__val${!value?" cp-display__val--placeholder":""}`}>{label_||placeholder}</span>
            <span className="cp-edit-icon">▾</span>
          </div>
      }
      <FieldStatus status={status} message={msg}/>
    </div>
  );
}

/* ─── TAG EDITOR ──────────────────────────────────────── */
function TagEditor({ tags=[], onSave, placeholder, label="", required=false }) {
  const [input,  setInput]  = useState("");
  const [status, setStatus] = useState(null);
  const [msg,    setMsg]    = useState("");
  const list = Array.isArray(tags)?tags:[];
  useEffect(()=>{ if(status==="saved"){ const t=setTimeout(()=>setStatus(null),3000); return ()=>clearTimeout(t); } },[status]);

  async function add(v) {
    const clean=(v||input).trim();
    if (!clean) return;
    if (hasProfanity(clean)){ setStatus("warn"); setMsg("Inappropriate language"); return; }
    if (list.includes(clean)){ setStatus("warn"); setMsg(`"${clean}" already added`); return; }
    const next=[...list,clean];
    setInput(""); setStatus("saving");
    try { await onSave(next); setStatus("saved"); }
    catch { setStatus("error"); setMsg("Save failed"); }
  }

  async function rm(t) {
    setStatus("saving");
    try { await onSave(list.filter(x=>x!==t)); setStatus("saved"); }
    catch { setStatus("error"); setMsg("Remove failed"); }
  }

  return (
    <div className="cp-tags">
      {label && (
        <div className="cp-field__header">
          <label className="cp-label">{label}{required&&<span className="cp-req-star">*</span>}</label>
          {required&&list.length===0&&<span className="cp-req-chip">Required</span>}
        </div>
      )}
      {list.length>0 && (
        <div className="cp-tags__list">
          {list.map(t=>(
            <span key={t} className="cp-tag">
              {t}<button onClick={()=>rm(t)} className="cp-tag__rm" title="Remove">×</button>
            </span>
          ))}
        </div>
      )}
      <div className="cp-tags__input-row">
        <input value={input} onChange={e=>{setInput(e.target.value);setStatus(null);}}
          onKeyDown={e=>{ if(e.key==="Enter"){e.preventDefault();add();} }}
          placeholder={placeholder} className="cp-input cp-input--inline"/>
        <button onClick={()=>add()} disabled={!input.trim()} className="cp-tag__add">+ Add</button>
      </div>
      <FieldStatus status={status} message={msg}/>
      {list.length===0&&<p className="cp-tags__empty-hint">No items added yet</p>}
    </div>
  );
}

/* ─── EDUCATION EDITOR ────────────────────────────────── */
function EducationEditor({ educations=[], onSave }) {
  const list = Array.isArray(educations)?educations:[];
  const [adding,   setAdding]   = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [status,   setStatus]   = useState(null);
  const [msg,      setMsg]      = useState("");
  const [errors,   setErrors]   = useState({});
  const empty = { degree:"", course:"", university:"", specialization:"", start_year:"", passing_year:"", percentage:"" };
  const [form, setForm] = useState(empty);

  useEffect(()=>{ if(status==="saved"){ const t=setTimeout(()=>setStatus(null),3000); return ()=>clearTimeout(t); } },[status]);

  function setField(k,v){ setForm(f=>({...f,[k]:v})); if(errors[k]) setErrors(e=>({...e,[k]:null})); }

  function validateForm() {
    const errs={};
    ["degree","start_year","passing_year","percentage","university","course"].forEach(k=>{
      const e=validate(k,form[k]); if(e) errs[k]=e;
    });
    if (form.start_year&&form.passing_year&&parseInt(form.start_year)>parseInt(form.passing_year))
      errs.passing_year="Passing year must be after start year";
    setErrors(errs);
    return Object.keys(errs).length===0;
  }

  async function saveForm() {
    if (!validateForm()) return;
    const next = editing!==null ? list.map((e,i)=>i===editing?form:e) : [...list,form];
    setStatus("saving");
    try { await onSave(next); setStatus("saved"); setAdding(false); setEditing(null); }
    catch { setStatus("error"); setMsg("Save failed"); }
  }

  async function removeEdu(i) {
    setStatus("saving");
    try { await onSave(list.filter((_,idx)=>idx!==i)); setStatus("saved"); }
    catch { setStatus("error"); setMsg("Remove failed"); }
  }

  function startAdd()  { setForm(empty); setErrors({}); setAdding(true); setEditing(null); }
  function startEdit(i){ setForm({...empty,...list[i]}); setErrors({}); setEditing(i); setAdding(false); }
  function cancel()    { setAdding(false); setEditing(null); setErrors({}); }

  return (
    <div className="cp-edu">
      {list.length===0&&!adding&&(
        <div className="cp-edu__empty">
          <span className="cp-edu__empty-icon">🎓</span>
          <div>
            <div className="cp-edu__empty-req">Required — add your education</div>
            <div>Click "Add education" below to get started</div>
          </div>
        </div>
      )}
      {list.map((edu,i)=>(
        <div key={i} className="cp-edu__item">
          <span className="cp-edu__item-icon">🎓</span>
          <div className="cp-edu__item-info">
            <div className="cp-edu__degree">{edu.degree}{edu.course&&<span className="cp-edu__course"> — {edu.course}</span>}</div>
            {edu.university&&<div className="cp-edu__uni">🏫 {edu.university}</div>}
            {edu.specialization&&<div className="cp-edu__spec">🔬 {edu.specialization}</div>}
            <div className="cp-edu__meta">
              {(edu.start_year||edu.passing_year)&&<span>📅 {edu.start_year||"?"} – {edu.passing_year||"Present"}</span>}
              {edu.percentage&&<span>📊 {edu.percentage}</span>}
            </div>
          </div>
          <div className="cp-edu__item-actions">
            <button onClick={()=>startEdit(i)} className="cp-btn cp-btn--ghost cp-btn--sm">✏ Edit</button>
            <button onClick={()=>removeEdu(i)} className="cp-btn cp-btn--danger cp-btn--sm">🗑</button>
          </div>
        </div>
      ))}

      {(adding||editing!==null)&&(
        <div className="cp-edu__form">
          <div className="cp-edu__form-title">{editing!==null?"Edit education":"Add education"}</div>
          <div className="cp-grid-2">
            {[
              { k:"degree",       l:"Degree *",           p:"B.Tech, MBA…",        type:"text" },
              { k:"course",       l:"Course / Stream",    p:"Computer Science…",   type:"text" },
              { k:"university",   l:"University / College",p:"e.g. IIT Delhi…",   type:"text", span:2 },
              { k:"specialization",l:"Specialization",    p:"Machine Learning…",   type:"text", span:2 },
              { k:"start_year",   l:"Start Year",         p:"2019",                type:"tel"  },
              { k:"passing_year", l:"Passing Year",       p:"2023",                type:"tel"  },
              { k:"percentage",   l:"Percentage / CGPA",  p:"85% or 8.5",          type:"text" },
            ].map(f=>(
              <div key={f.k} className="cp-field" style={{ gridColumn:f.span?`span ${f.span}`:undefined }}>
                <label className="cp-label">{f.l}</label>
                <input type={f.type} className={`cp-input${errors[f.k]?" cp-input--err":""}`}
                  placeholder={f.p} value={form[f.k]||""}
                  onChange={e=>{ let v=e.target.value; if(f.type==="tel") v=v.replace(/\D/g,"").slice(0,4); setField(f.k,v); }}/>
                {errors[f.k]&&<span className="cp-field-inline-err">⚠ {errors[f.k]}</span>}
              </div>
            ))}
          </div>
          <FieldStatus status={status} message={msg}/>
          <div className="cp-edu__form-actions">
            <button onClick={saveForm} disabled={!form.degree||status==="saving"} className="cp-btn cp-btn--primary">
              {status==="saving"?<><span className="cp-spin cp-spin--xs"/>Saving…</>:editing!==null?"Save changes":"Add education"}
            </button>
            <button onClick={cancel} className="cp-btn cp-btn--ghost">Cancel</button>
          </div>
        </div>
      )}
      {!adding&&editing===null&&(
        <button onClick={startAdd} className="cp-edu__add-btn">+ Add education</button>
      )}
    </div>
  );
}

/* ─── KYC PHOTO UPLOAD ────────────────────────────────── */
/* ─── PROJECTS EDITOR ───────────────────────────────────────────────────── */
function ProjectsEditor({ projects=[], onSave }) {
  const list = Array.isArray(projects) ? projects : [];
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState("");
  const [errors, setErrors] = useState({});
  const empty = { title:"", description:"", project_url:"", image_url:"", technologiesText:"" };
  const [form, setForm] = useState(empty);

  useEffect(()=>{ if(status==="saved"){ const t=setTimeout(()=>setStatus(null),3000); return ()=>clearTimeout(t); } },[status]);

  function setField(k,v){ setForm(f=>({...f,[k]:v})); if(errors[k]) setErrors(e=>({...e,[k]:null})); }

  function validateForm() {
    const errs = {};
    if (!form.title?.trim()) errs.title = "Project title is required";
    if (form.project_url?.trim() && !/^https?:\/\/.+/i.test(form.project_url.trim())) errs.project_url = "Use a valid http/https URL";
    if (form.image_url?.trim() && !/^https?:\/\/.+/i.test(form.image_url.trim())) errs.image_url = "Use a valid http/https URL";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function normalizeProject(data) {
    const technologies = String(data.technologiesText || "")
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);
    return {
      title: data.title.trim(),
      description: data.description.trim(),
      project_url: data.project_url.trim(),
      image_url: data.image_url.trim(),
      technologies,
    };
  }

  async function saveForm() {
    if (!validateForm()) return;
    const nextItem = normalizeProject(form);
    const next = editing !== null ? list.map((e,i)=>i===editing ? nextItem : e) : [...list, nextItem];
    setStatus("saving");
    try { await onSave(next); setStatus("saved"); setAdding(false); setEditing(null); }
    catch { setStatus("error"); setMsg("Save failed"); }
  }

  async function removeProject(i) {
    setStatus("saving");
    try { await onSave(list.filter((_,idx)=>idx!==i)); setStatus("saved"); }
    catch { setStatus("error"); setMsg("Remove failed"); }
  }

  function startAdd()  { setForm(empty); setErrors({}); setAdding(true); setEditing(null); }
  function startEdit(i){
    const p = list[i] || {};
    setForm({
      title: p.title || "",
      description: p.description || "",
      project_url: p.project_url || "",
      image_url: p.image_url || "",
      technologiesText: Array.isArray(p.technologies) ? p.technologies.join(", ") : (p.technologies || ""),
    });
    setErrors({});
    setEditing(i);
    setAdding(false);
  }
  function cancel(){ setAdding(false); setEditing(null); setErrors({}); }

  return (
    <div className="cp-projects">
      {list.length===0&&!adding&&(
        <div className="cp-projects__empty">
          <span className="cp-projects__empty-icon">🧩</span>
          <div>
            <div className="cp-projects__empty-req">Showcase your projects</div>
            <div>Add live links and a cover image so recruiters can view your work.</div>
          </div>
        </div>
      )}

      {list.map((project, i) => (
        <div key={i} className="cp-projects__item">
          <div className="cp-projects__thumb">
            {project.image_url ? <img src={project.image_url} alt={project.title || "Project"} /> : <span>🖼️</span>}
          </div>
          <div className="cp-projects__info">
            <div className="cp-projects__title">{project.title || "Untitled project"}</div>
            {project.description && <div className="cp-projects__desc">{project.description}</div>}
            {Array.isArray(project.technologies) && project.technologies.length > 0 && (
              <div className="cp-projects__chips">
                {project.technologies.map((tech) => <span key={tech} className="cp-projects__chip">{tech}</span>)}
              </div>
            )}
            <div className="cp-projects__meta">
              {project.project_url && <a href={project.project_url} target="_blank" rel="noreferrer">Open project →</a>}
            </div>
          </div>
          <div className="cp-projects__actions">
            <button onClick={()=>startEdit(i)} className="cp-btn cp-btn--ghost cp-btn--sm">✏ Edit</button>
            <button onClick={()=>removeProject(i)} className="cp-btn cp-btn--danger cp-btn--sm">🗑</button>
          </div>
        </div>
      ))}

      {(adding||editing!==null)&&(
        <div className="cp-projects__form">
          <div className="cp-projects__form-title">{editing!==null ? "Edit project" : "Add project"}</div>
          <div className="cp-grid-2">
            <div className="cp-field" style={{ gridColumn:"1 / -1" }}>
              <label className="cp-label">Project title *</label>
              <input className={`cp-input${errors.title?" cp-input--err":""}`} value={form.title} placeholder="e.g. Portfolio Website" onChange={e=>setField("title", e.target.value)} />
              {errors.title && <span className="cp-field-inline-err">⚠ {errors.title}</span>}
            </div>
            <div className="cp-field" style={{ gridColumn:"1 / -1" }}>
              <label className="cp-label">Description</label>
              <textarea className="cp-input cp-projects__textarea" value={form.description} placeholder="What does the project do?" onChange={e=>setField("description", e.target.value)} />
            </div>
            <div className="cp-field">
              <label className="cp-label">Project link</label>
              <input className={`cp-input${errors.project_url?" cp-input--err":""}`} value={form.project_url} placeholder="https://..." onChange={e=>setField("project_url", e.target.value)} />
              {errors.project_url && <span className="cp-field-inline-err">⚠ {errors.project_url}</span>}
            </div>
            <div className="cp-field">
              <label className="cp-label">Cover image URL</label>
              <input className={`cp-input${errors.image_url?" cp-input--err":""}`} value={form.image_url} placeholder="https://..." onChange={e=>setField("image_url", e.target.value)} />
              {errors.image_url && <span className="cp-field-inline-err">⚠ {errors.image_url}</span>}
            </div>
            <div className="cp-field" style={{ gridColumn:"1 / -1" }}>
              <label className="cp-label">Technologies</label>
              <input className="cp-input" value={form.technologiesText} placeholder="React, Node.js, MongoDB" onChange={e=>setField("technologiesText", e.target.value)} />
            </div>
          </div>
          <FieldStatus status={status} message={msg}/>
          <div className="cp-projects__form-actions">
            <button onClick={saveForm} disabled={!form.title||status==="saving"} className="cp-btn cp-btn--primary">
              {status==="saving"?<><span className="cp-spin cp-spin--xs"/>Saving…</>:editing!==null?"Save changes":"Add project"}
            </button>
            <button onClick={cancel} className="cp-btn cp-btn--ghost">Cancel</button>
          </div>
        </div>
      )}
      {!adding&&editing===null&&(
        <button onClick={startAdd} className="cp-projects__add-btn">+ Add project</button>
      )}
    </div>
  );
}

function KYCPhotoUpload({ field, existing, onUploaded, required=false, label="Document", onPointsEarned }) {
  const [uploading, setUploading] = useState(false);
  const [preview,   setPreview]   = useState(existing||null);
  const [error,     setError]     = useState("");
  const [dragOver,  setDragOver]  = useState(false);
  const inputRef = useRef();
  const SERVER = (process.env.REACT_APP_API_URL||"http://localhost:5001").replace(/\/api$/,"");

  useEffect(()=>{ if(existing&&!preview) setPreview(existing); },[existing]); // eslint-disable-line

  async function handleFile(file) {
    if (!file) return;
    const allowed=["image/jpeg","image/jpg","image/png","application/pdf"];
    if (!allowed.includes(file.type)){ setError("Only JPG, PNG or PDF allowed"); return; }
    if (file.size>5*1024*1024){ setError("Max file size is 5MB"); return; }
    setError(""); setUploading(true);
    if (file.type!=="application/pdf") setPreview(URL.createObjectURL(file));
    const fd=new FormData(); fd.append(field,file);
    try {
      const res=await axios.post("/api/candidate/upload-kyc",fd,{
        headers:{...authHeaders(),"Content-Type":"multipart/form-data"},
      });
      if (res.data.success){
        onUploaded(res.data[field]);
        if (file.type==="application/pdf") setPreview("pdf");
        // Award points after successful upload
        if (onPointsEarned) onPointsEarned(field);
      } else throw new Error(res.data.error||"Upload failed");
    } catch(err) {
      setError(err?.response?.data?.error||err.message||"Upload failed");
      setPreview(existing||null);
    } finally { setUploading(false); }
  }

  const isPDF = preview==="pdf"||(typeof preview==="string"&&preview.includes(".pdf")&&!preview.startsWith("blob:"));
  const missing = required&&!preview;

  return (
    <div className="cp-kyc-upload">
      {preview ? (
        <div className={`cp-kyc-upload__zone cp-kyc-upload__zone--uploaded`}>
          <div className="cp-kyc-upload__uploaded-bar">
            <span className="cp-kyc-upload__uploaded-text">✓ {label} uploaded</span>
            <button onClick={()=>inputRef.current?.click()} className="cp-kyc-upload__change">Change</button>
          </div>
          {isPDF
            ? <div className="cp-kyc-upload__pdf-preview">
                <div style={{fontSize:32}}>📄</div>
                <div style={{fontSize:12,fontWeight:600,color:"var(--cp-green)"}}>PDF uploaded</div>
                {existing&&!preview.startsWith("blob:")&&(
                  <a href={`${SERVER}${existing}`} target="_blank" rel="noreferrer" className="cp-kyc-upload__view-link">View →</a>
                )}
              </div>
            : <img src={preview.startsWith("blob:")?preview:`${SERVER}${preview}`}
                alt={`${label} doc`} className="cp-kyc-upload__img"/>
          }
          {uploading&&<div className="cp-kyc-upload__uploading"><span className="cp-spin cp-spin--sm"/>Uploading…</div>}
        </div>
      ) : (
        <div className={`cp-kyc-upload__zone${missing?" cp-kyc-upload__zone--required":""}${dragOver?" cp-kyc-upload__zone--dragover":""}`}
          onClick={()=>!uploading&&inputRef.current?.click()}
          onDragOver={e=>{e.preventDefault();setDragOver(true);}}
          onDragLeave={()=>setDragOver(false)}
          onDrop={e=>{e.preventDefault();setDragOver(false);handleFile(e.dataTransfer.files[0]);}}>
          {uploading
            ? <div className="cp-kyc-upload__uploading"><span className="cp-spin cp-spin--lg"/><span>Uploading…</span></div>
            : <>
                <div className="cp-kyc-upload__icon">{dragOver?"📂":missing?"⚠":"📷"}</div>
                <div className="cp-kyc-upload__label">{dragOver?"Drop to upload":missing?`${label} photo required`:`Upload ${label} photo`}</div>
                <div className="cp-kyc-upload__hint">Drag & drop or click · JPG, PNG, PDF · max 5MB</div>
                <button className="cp-kyc-upload__btn">Choose file</button>
              </>
          }
        </div>
      )}
      {error&&(
        <div className="cp-kyc-upload__error">
          ⚠ {error}
          <button onClick={()=>setError("")} className="cp-kyc-upload__error-close">✕</button>
        </div>
      )}
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" hidden onChange={e=>handleFile(e.target.files[0])}/>
    </div>
  );
}

/* ─── USERNAME MODAL ──────────────────────────────────── */
function UsernameModal({ currentUsername, firstName, lastName, onClose, onSave }) {
  const [input,    setInput]    = useState("");
  const [status,   setStatus]   = useState("idle");
  const [error,    setError]    = useState("");
  const [suggs,    setSuggs]    = useState([]);
  const [loadSugg, setLoadSugg] = useState(false);
  const debRef   = useRef(null);
  const inputRef = useRef(null);

  useEffect(()=>{ generateSuggestions(); },[]);// eslint-disable-line

  async function generateSuggestions() {
    setLoadSugg(true);
    const base=`${firstName||""}${lastName||""}`.toLowerCase().replace(/[^a-z0-9]/g,"").slice(0,14);
    const candidates=[base,`${base}${Math.floor(Math.random()*900+100)}`,`${base}_${Math.floor(Math.random()*99+1)}`,`the_${base}`,`${base}_pro`]
      .filter((v,i,a)=>v.length>=4&&a.indexOf(v)===i).slice(0,4);
    const available=[];
    for (const c of candidates) {
      try {
        const res=await fetch(`${API}/candidate/check-username?username=${encodeURIComponent(c)}`);
        const d=await res.json();
        if (!d.exists) available.push(c);
      } catch { available.push(c); }
      if (available.length>=3) break;
    }
    setSuggs(available); setLoadSugg(false);
  }

  function validateUsername(val) {
    if (!val) return "";
    if (val.length<4) return "Min 4 characters";
    if (val.length>20) return "Max 20 characters";
    if (!/^[a-z0-9_]+$/.test(val)) return "Only lowercase letters, numbers, underscores";
    if (val===currentUsername) return "That's already your username";
    return "";
  }

  function handleChange(val) {
    const clean=val.toLowerCase().replace(/[^a-z0-9_]/g,"");
    setInput(clean); setStatus("idle"); setError("");
    const err=validateUsername(clean);
    if (err){ setStatus("invalid"); setError(err); return; }
    clearTimeout(debRef.current); setStatus("checking");
    debRef.current=setTimeout(async()=>{
      try {
        const res=await fetch(`${API}/candidate/check-username?username=${encodeURIComponent(clean)}`);
        const d=await res.json();
        if(d.exists){ setStatus("taken"); setError("Username already taken"); }
        else { setStatus("available"); setError(""); }
      } catch { setStatus("available"); }
    },600);
  }

  async function handleSave() {
    if (status!=="available") return;
    setStatus("saving");
    try {
      await axios.patch(`${API}/candidate/username`,{ username:input },{headers:authHeaders()});
      onSave(input); onClose();
    } catch(err) {
      setStatus("taken"); setError(err?.response?.data?.error||"Could not update. Try again.");
    }
  }

  const statusColor={ available:"var(--cp-green)", taken:"var(--cp-red)", invalid:"var(--cp-amber)", checking:"var(--cp-ink-4)", idle:"", saving:"var(--cp-ink-4)" }[status];

  return (
    <div className="cp-modal-bg" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="cp-modal cp-modal--username">
        <div className="cp-modal__head">
          <div className="cp-modal__head-icon">@</div>
          <div><h3>Change username</h3><p className="cp-modal__head-sub">Choose a unique identity on the platform</p></div>
          <button onClick={onClose} className="cp-modal__close">×</button>
        </div>
        <div className="cp-username__current">
          <span className="cp-username__current-label">Current username</span>
          <span className="cp-username__current-val">@{currentUsername||"not set"}</span>
        </div>
        <div className="cp-username__input-wrap">
          <span className="cp-username__at">@</span>
          <input ref={inputRef} autoFocus className="cp-username__input"
            placeholder="your_username" value={input} maxLength={20}
            onChange={e=>handleChange(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&status==="available") handleSave(); }}/>
          <span className="cp-username__status-icon" style={{color:statusColor}}>
            {status==="checking"||status==="saving" ? <span className="cp-spin cp-spin--xs"/> :
              status==="available"?"✅":status==="taken"?"❌":status==="invalid"?"⚠️":""}
          </span>
        </div>
        <div className="cp-username__feedback" style={{color:statusColor,minHeight:18}}>
          {error&&<span>{error}</span>}
          {status==="available"&&!error&&<span style={{color:"var(--cp-green)"}}>✅ Available!</span>}
          {status==="checking"&&<span>Checking availability…</span>}
        </div>
        <div className="cp-username__rules">
          <span className={input.length>=4?"rule--ok":""}>✓ Min 4 chars</span>
          <span className={/^[a-z0-9_]*$/.test(input)&&input?"rule--ok":""}>✓ Letters, numbers, _</span>
          <span className={input.length<=20?"rule--ok":""}>✓ Max 20 chars</span>
        </div>
        {suggs.length>0&&(
          <div className="cp-username__sugg-section">
            <div className="cp-username__sugg-title">Available suggestions {loadSugg&&<span className="cp-spin cp-spin--xs" style={{marginLeft:6}}/>}</div>
            <div className="cp-username__sugg-list">
              {suggs.map(s=>(
                <button key={s} onClick={()=>{ setInput(s); setStatus("available"); setError(""); inputRef.current?.focus(); }}
                  className={`cp-username__sugg-chip${input===s?" cp-username__sugg-chip--active":""}`}>
                  @{s}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="cp-username__notice">⏳ Username can only be changed once every 3 months.</div>
        <div className="cp-modal__actions">
          <button onClick={handleSave} disabled={status!=="available"} className="cp-btn cp-btn--primary" style={{flex:2}}>
            {status==="saving"?<><span className="cp-spin cp-spin--xs"/>Saving…</>:"Save username"}
          </button>
          <button onClick={onClose} className="cp-btn cp-btn--ghost" style={{flex:1}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─── UPDATE MODAL (email / phone) ───────────────────── */
function UpdateModal({ type, current, onClose, onSave }) {
  const [val, setVal]   = useState("");
  const [otp, setOtp]   = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState("");
  const label = type==="email"?"Email":"Mobile number";

  function validateInput() {
    if (!val.trim()) return `Enter ${label}`;
    if (type==="email"&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim())) return "Invalid email";
    if (type!=="email"&&val.replace(/\D/g,"").length<10) return "Min 10 digits";
    return "";
  }

  function send() {
    const e=validateInput(); if(e){ setErr(e); return; }
    setErr(""); setBusy(true);
    setTimeout(()=>{ setStep(2); setBusy(false); },1200);
  }

  function verify() {
    if (otp.length<4){ setErr("Enter the OTP"); return; }
    setErr(""); setBusy(true);
    setTimeout(()=>{ onSave(type,val); setBusy(false); onClose(); },800);
  }

  return (
    <div className="cp-modal-bg" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="cp-modal">
        <div className="cp-modal__head">
          <h3>Update {label}</h3>
          <button onClick={onClose} className="cp-modal__close">×</button>
        </div>
        <p className="cp-modal__sub">Current: <strong>{current||"Not set"}</strong></p>
        {step===1 ? (
          <>
            <input type={type==="email"?"email":"tel"} className="cp-input" autoFocus
              placeholder={`New ${label}`} value={val}
              onChange={e=>{setVal(e.target.value);setErr("");}}
              onKeyDown={e=>e.key==="Enter"&&send()}/>
            {err&&<p className="cp-modal__err">⚠ {err}</p>}
            <button className="cp-btn cp-btn--primary" onClick={send} disabled={busy} style={{width:"100%",justifyContent:"center"}}>
              {busy?<><span className="cp-spin cp-spin--sm"/>Sending…</>:"Send verification code"}
            </button>
          </>
        ) : (
          <>
            <p className="cp-modal__sub">Code sent to <strong>{val}</strong></p>
            <input type="text" maxLength={6} className="cp-input cp-input--otp" autoFocus
              placeholder="Enter OTP" value={otp}
              onChange={e=>{setOtp(e.target.value);setErr("");}}
              onKeyDown={e=>e.key==="Enter"&&verify()}/>
            {err&&<p className="cp-modal__err">⚠ {err}</p>}
            <div style={{display:"flex",gap:10}}>
              <button className="cp-btn cp-btn--ghost" style={{flex:1}} onClick={()=>setStep(1)}>← Back</button>
              <button className="cp-btn cp-btn--primary" style={{flex:2}} onClick={verify} disabled={busy}>
                {busy?<><span className="cp-spin cp-spin--sm"/>Verifying…</>:"Verify & update"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── REWARDS PAGE ────────────────────────────────────── */
function RewardsPage({ profile, earnedActions, totalPoints }) {
  const lv = getLevelInfo(totalPoints);
  const milestoneIdx = MILESTONES.findIndex(m=>totalPoints<m.pts);
  const nextMilestone = milestoneIdx!==-1 ? MILESTONES[milestoneIdx] : null;
  const prevMilestone = milestoneIdx>0 ? MILESTONES[milestoneIdx-1] : null;
  const mFrom = prevMilestone?.pts||0;
  const mTo   = nextMilestone?.pts||500;
  const mPct  = nextMilestone ? Math.round(((totalPoints-mFrom)/(mTo-mFrom))*100) : 100;

  const categories = {
    basic:    "Basic profile",
    kyc:      "KYC documents",
    career:   "Career profile",
    skill:    "Skills & quizzes",
    hiring:   "Hiring funnel",
    referral: "Referrals",
    events:   "Career talks",
    bonus:    "Bonus actions",
  };

  return (
    <div className="cp-rewards">
      {/* Level card */}
      <div className="cp-level-card">
        <div className="cp-level-card__left">
          <span className="cp-level-card__emoji">{lv.emoji}</span>
          <div>
            <div className="cp-level-card__num">Level {["Rookie","Starter","Explorer","Achiever","Performer","Champion"].indexOf(lv.title)+1}</div>
            <div className="cp-level-card__title">{lv.title}</div>
          </div>
        </div>
        <div className="cp-level-card__right">
          <div className="cp-level-card__xp-row">
            <span>{totalPoints} pts earned</span>
            {nextMilestone&&<span>Next: {nextMilestone.pts} pts</span>}
          </div>
          <div className="cp-level-bar"><div className="cp-level-bar__fill" style={{width:`${mPct}%`}}/></div>
          {nextMilestone&&<div className="cp-level-card__next">Earn {nextMilestone.pts-totalPoints} more pts → <strong>{nextMilestone.label}</strong> — {nextMilestone.reward}</div>}
        </div>
        <div className="cp-level-card__pts">
          <div className="cp-level-card__pts-num">{totalPoints}</div>
          <div className="cp-level-card__pts-label">Total points · 1pt = ₹1</div>
        </div>
      </div>

      {/* Milestones */}
      <div className="cp-section-box">
        <div className="cp-section-box__title">Reward milestones</div>
        <div className="cp-milestones">
          {MILESTONES.map(m=>{
            const reached = totalPoints>=m.pts;
            const isCurrent = nextMilestone?.pts===m.pts;
            return (
              <div key={m.pts} className={`cp-milestone${reached?" cp-milestone--done":isCurrent?" cp-milestone--next":""}`}>
                <div className="cp-milestone__badge">{m.badge}</div>
                <div className="cp-milestone__label">{m.label}</div>
                <div className="cp-milestone__pts">{m.pts} pts</div>
                <div className="cp-milestone__reward">{m.reward}</div>
                {reached&&<div className="cp-milestone__check">✓ Unlocked</div>}
                {isCurrent&&<div className="cp-milestone__progress">{mPct}% there</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Points breakdown table */}
      <div className="cp-section-box">
        <div className="cp-section-box__title">Points you can earn</div>
        <div className="cp-section-box__desc">Complete these actions to earn points. 1 point = ₹1, max redemption ₹500.</div>
        <div className="cp-rewards-table-wrap">
          <table className="cp-rewards-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Category</th>
                <th>Points</th>
                <th>Timing</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(REWARD_ACTIONS).map(([key,action])=>{
                const done = earnedActions.includes(key);
                return (
                  <tr key={key} className={done?"cp-rewards-table__row--done":""}>
                    <td>{action.label}</td>
                    <td><span className="cp-cat-chip">{categories[action.category]||action.category}</span></td>
                    <td><span className="cp-pts-chip">+{action.points} pts</span></td>
                    <td style={{fontSize:11,color:"var(--cp-ink-4)"}}>{action.instant?"Instant":"After verification"}</td>
                    <td>
                      <span className={`cp-status-chip${done?" cp-status-chip--done":" cp-status-chip--todo"}`}>
                        {done?"✓ Earned":"Pending"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Redemption info */}
      <div className="cp-section-box">
        <div className="cp-section-box__title">How to redeem</div>
        <div className="cp-redeem-options">
          {[
            { icon:"💸", title:"Cash transfer",    sub:"UPI / Paytm / Bank transfer",    min:"100 pts",  time:"1–2 days" },
            { icon:"🎁", title:"Physical gifts",   sub:"Tech, books, lifestyle items",   min:"100 pts",  time:"3–7 days" },
            { icon:"🛍", title:"Vouchers",         sub:"Amazon, Flipkart, restaurants",  min:"100 pts",  time:"Instant"  },
            { icon:"🎓", title:"Career tools",     sub:"Courses, mock interviews, ATS",  min:"100 pts",  time:"Instant"  },
          ].map(o=>(
            <div key={o.title} className="cp-redeem-card">
              <div className="cp-redeem-card__icon">{o.icon}</div>
              <div className="cp-redeem-card__title">{o.title}</div>
              <div className="cp-redeem-card__sub">{o.sub}</div>
              <div className="cp-redeem-card__meta">Min: {o.min} · {o.time}</div>
            </div>
          ))}
        </div>
        <div className="cp-redeem-notice">
          You have <strong>{totalPoints} points</strong> (≈ ₹{totalPoints}).
          {totalPoints>=100
            ? " You can redeem now! Contact support to initiate your reward."
            : ` You need ${100-totalPoints} more points to unlock redemption.`}
        </div>
      </div>
    </div>
  );
}

/* ─── JOBS TAB ────────────────────────────────────────── */
const APP_STATUS = {
  pending:     { label:"Under Review", color:"#7c3aed", bg:"rgba(124,58,237,0.08)" },
  shortlisted: { label:"Shortlisted",  color:"#0891b2", bg:"rgba(8,145,178,0.08)"  },
  interview:   { label:"Interview",    color:"#263012", bg:"rgba(217,119,6,0.08)"   },
  hired:       { label:"Hired 🎉",     color:"#059669", bg:"rgba(5,150,105,0.08)"  },
  rejected:    { label:"Not Selected", color:"#263012", bg:"rgba(220,38,38,0.08)"  },
};

/* ─── SAVED JOB CARD ─────────────────────────────────────── */
const LOGO_COLORS_CP = ["#263012","#263012","#111111","#1d240e","#263012","#263012","#2a2a2a","#5c774a","#12160e","#6b7280"];
function cpCompanyColor(n) {
  return LOGO_COLORS_CP[(n||"?").charCodeAt(0) % LOGO_COLORS_CP.length];
}

// function SavedJobCard({ job, onRemove, onApply, isApplied, isApplying }) {
//   const [expanded, setExpanded] = useState(false);
//   const color = cpCompanyColor(job.company_name || job.company || "J");
//   const logoSrc = resolveLogoSrc(job.company_logo || job.logo || "");
//   const title = job.title || "—";
//   const company = job.company_name || job.company || "—";
//   const jid = job.job_id || job.id;

//   const tags = (() => {
//     try {
//       const raw = job.skills || job.tags || "";
//       if (Array.isArray(raw)) return raw;
//       if (typeof raw === "string" && raw.startsWith("[")) return JSON.parse(raw);
//       if (typeof raw === "string" && raw.trim()) return raw.split(",").map(s => s.trim()).filter(Boolean);
//       return [];
//     } catch { return []; }
//   })();

//   return (
//     <div className="cp-saved-jcard" style={{ borderLeftColor: color, "--sjc-color": color }}>
//       {/* ── header ── */}
//       <div className="cp-saved-jcard__head">
//         <div className="cp-saved-jcard__logo" style={{ background: `${color}18`, borderColor: `${color}30` }}>
//           {logoSrc
//             ? <img src={logoSrc} alt={company} style={{ width:"100%", height:"100%", objectFit:"contain", borderRadius:6 }}/>
//             : <span style={{ color, fontWeight:900, fontSize:"1rem" }}>{company[0]?.toUpperCase()||"J"}</span>}
//         </div>
//         <div className="cp-saved-jcard__info">
//           <div className="cp-saved-jcard__title">{title}</div>
//           <div className="cp-saved-jcard__company">{company}</div>
//           <div className="cp-saved-jcard__meta">
//             {job.location && <span className="cp-saved-jcard__chip cp-saved-jcard__chip--loc">📍 {job.location}</span>}
//             {(job.type || job.job_type) && <span className="cp-saved-jcard__chip">💼 {job.type || job.job_type}</span>}
//             {job.experience && <span className="cp-saved-jcard__chip cp-saved-jcard__chip--exp">⚡ {job.experience}</span>}
//             {job.salary && job.salary !== "Not disclosed" && (
//               <span className="cp-saved-jcard__chip cp-saved-jcard__chip--salary">₹ {job.salary}</span>
//             )}
//             {job.openings > 0 && (
//               <span className="cp-saved-jcard__chip" style={{ color, borderColor: `${color}40`, background: `${color}0d` }}>
//                 👥 {job.openings} opening{job.openings !== 1 ? "s" : ""}
//               </span>
//             )}
//           </div>
//         </div>
//         <div className="cp-saved-jcard__badge-col">
//           {isApplied && <span className="cp-saved-jcard__applied-badge">✓ Applied</span>}
//         </div>
//       </div>

//       {/* ── expanded description + tags ── */}
//       {expanded && (
//         <div className="cp-saved-jcard__body">
//           {job.description && (
//             <div className="cp-saved-jcard__desc">{job.description}</div>
//           )}
//           {tags.length > 0 && (
//             <div className="cp-saved-jcard__tags">
//               {tags.slice(0, 10).map((t, i) => (
//                 <span key={i} className="cp-saved-jcard__tag">{t}</span>
//               ))}
//               {tags.length > 10 && (
//                 <span className="cp-saved-jcard__tag cp-saved-jcard__tag--more">+{tags.length - 10}</span>
//               )}
//             </div>
//           )}
//         </div>
//       )}

//       {/* ── actions ── */}
//       <div className="cp-saved-jcard__actions">
//         <button
//           className="cp-saved-jcard__btn cp-saved-jcard__btn--ghost"
//           onClick={() => setExpanded(v => !v)}
//         >
//           {expanded ? "▲ Hide details" : "▼ View details"}
//         </button>

//         {isApplied ? (
//           <button className="cp-saved-jcard__btn cp-saved-jcard__btn--applied" disabled>
//             ✓ Applied
//           </button>
//         ) : isApplying ? (
//           <button className="cp-saved-jcard__btn cp-saved-jcard__btn--apply" disabled style={{ opacity: 0.7 }}>
//             <span className="cp-spin cp-spin--xs" style={{ marginRight: 5 }}/>Applying…
//           </button>
//         ) : (
//           <button
//             className="cp-saved-jcard__btn cp-saved-jcard__btn--apply"
//             style={{ borderColor: color, color }}
//             onClick={() => onApply(job)}
//           >
//             Apply Now →
//           </button>
//         )}

//         <button
//           className="cp-saved-jcard__btn cp-saved-jcard__btn--remove"
//           onClick={() => onRemove(jid, title)}
//           title="Remove from saved"
//         >
//           🗑 Remove
//         </button>
//       </div>
//     </div>
//   );
// }

/* ─── APPLY CONFIRM MODAL ─────────────────────────────────── */
function ApplyConfirmModal({ job, onClose, onConfirm, applying }) {
  const color = cpCompanyColor(job.company_name || job.company || "J");
  return (
    <div className="cp-modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cp-modal" style={{ maxWidth: 440 }}>
        <div className="cp-modal__head">
          <div className="cp-modal__head-icon" style={{ background: `${color}18`, color }}>
            💼
          </div>
          <div>
            <h3>Apply for this job?</h3>
            <p className="cp-modal__head-sub">{job.title} · {job.company_name || job.company}</p>
          </div>
          <button onClick={onClose} className="cp-modal__close">×</button>
        </div>
        <div style={{ padding: "14px 22px 6px", fontSize: 13.5, color: "var(--cp-ink-4)", lineHeight: 1.7 }}>
          Your saved profile and resume will be sent to the employer. Make sure your profile is complete before applying.
        </div>
        <div className="cp-modal__actions" style={{ padding: "14px 22px 22px" }}>
          <button
            className="cp-btn cp-btn--primary"
            style={{ flex: 2, justifyContent: "center" }}
            onClick={onConfirm}
            disabled={applying}
          >
            {applying ? <><span className="cp-spin cp-spin--xs" style={{ marginRight: 6 }}/>Applying…</> : "Confirm & Apply →"}
          </button>
          <button className="cp-btn cp-btn--ghost" style={{ flex: 1 }} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── JOBS TAB ────────────────────────────────────────────── */
function JobsTab({ profileApplied = [] }) {
  const [subTab,       setSubTab]       = useState("applied");
  const [applied,      setApplied]      = useState([]);
  const [saved,        setSaved]        = useState([]);
  const [aLoad,        setALoad]        = useState(true);
  const [sLoad,        setSLoad]        = useState(true);
  const [aError,       setAError]       = useState(null);
  const [sError,       setSError]       = useState(null);
  const [toast,        setToast]        = useState(null);
  const [applyingId,   setApplyingId]   = useState(null);
  const [confirmJob,   setConfirmJob]   = useState(null);
  const [appliedSet,   setAppliedSet]   = useState(new Set());

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  /* load applied */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setALoad(true); setAError(null);
      try {
        const res = await axios.get(`${API}/applications/mine`, { headers: authHeaders() });
        if (cancelled) return;
        if (res.data?.success) {
          const ORDER = { hired:0, interview:1, shortlisted:2, pending:3, rejected:4 };
          const list = [...(res.data.data || [])].sort((a, b) => (ORDER[a.status]??3) - (ORDER[b.status]??3));
          setApplied(list);
          setAppliedSet(new Set(list.map(j => j.job_id || j.id)));
        } else {
          setApplied(profileApplied);
          setAppliedSet(new Set(profileApplied.map(j => j.job_id || j.id)));
        }
      } catch {
        if (!cancelled) {
          setAError("Could not load applications.");
          setApplied(profileApplied);
          setAppliedSet(new Set(profileApplied.map(j => j.job_id || j.id)));
        }
      } finally { if (!cancelled) setALoad(false); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  /* load saved */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSLoad(true); setSError(null);
      try {
        const res = await axios.get(`${API}/saved-jobs`, { headers: authHeaders() });
        if (!cancelled) {
          if (res.data?.success) setSaved(res.data.data || []);
          else setSError("Could not load saved jobs.");
        }
      } catch { if (!cancelled) setSError("Could not load saved jobs."); }
      finally { if (!cancelled) setSLoad(false); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const removeSaved = async (jobId, title) => {
    try {
      await axios.delete(`${API}/saved-jobs`, { headers: authHeaders(), data: { job_id: jobId } });
      setSaved(p => p.filter(j => (j.job_id || j.id) !== jobId));
      showToast(`Removed "${title}"`);
    } catch { showToast("Failed to remove", "error"); }
  };

  const handleApplyClick = (job) => {
    const jid = job.job_id || job.id;
    if (appliedSet.has(jid)) return;
    setConfirmJob(job);
  };

  const confirmApply = async () => {
    if (!confirmJob) return;
    const jid = confirmJob.job_id || confirmJob.id;
    setApplyingId(jid);
    setConfirmJob(null);
    try {
      await axios.post(`${API}/applications`, { job_id: jid }, { headers: authHeaders() });
      setAppliedSet(prev => new Set([...prev, jid]));
      showToast(`✅ Applied to "${confirmJob.title}"!`);
    } catch (err) {
      const msg = err?.response?.data?.error || "Application failed";
      if (msg.toLowerCase().includes("already")) {
        setAppliedSet(prev => new Set([...prev, jid]));
        showToast("You already applied to this job");
      } else {
        showToast(msg, "error");
      }
    } finally { setApplyingId(null); }
  };

  return (
    <div className="cp-jobs">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      {confirmJob && (
        <ApplyConfirmModal
          job={confirmJob}
          applying={applyingId === (confirmJob.job_id || confirmJob.id)}
          onClose={() => setConfirmJob(null)}
          onConfirm={confirmApply}
        />
      )}

      <div className="cp-subtabs">
        <button onClick={() => setSubTab("applied")} className={`cp-subtab${subTab === "applied" ? " cp-subtab--active" : ""}`}>
          Applied <span className="cp-subtab__count">{aLoad ? "…" : applied.length}</span>
        </button>
        <button onClick={() => setSubTab("saved")} className={`cp-subtab${subTab === "saved" ? " cp-subtab--active" : ""}`}>
          Saved <span className="cp-subtab__count">{sLoad ? "…" : saved.length}</span>
        </button>
      </div>

      {/* ── APPLIED ── */}
      {subTab === "applied" && (
        <div className="cp-jobs__list">
          {aLoad && <div className="cp-jobs__loading"><span className="cp-spin cp-spin--lg" /><p>Loading applications…</p></div>}
          {!aLoad && aError && <div className="cp-jobs__error">⚠ {aError}</div>}
          {!aLoad && !aError && applied.length === 0 && (
            <div className="cp-jobs__empty">
              <div className="cp-jobs__empty-icon">🚀</div>
              <h4>No applications yet</h4>
              <p>Start applying and track your journey here</p>
              <a href="/employee/jobs" className="cp-btn cp-btn--primary">Browse jobs →</a>
            </div>
          )}
          {!aLoad && !aError && applied.map((j, i) => {
            const st = j.status || "pending";
            const cfg = APP_STATUS[st] || APP_STATUS.pending;
            const co = j.company_name || j.company || "—";
            const logoSrc = resolveLogoSrc(j.company_logo || "");
            const daysAgo = j.applied_at ? Math.max(0, Math.floor((Date.now() - new Date(j.applied_at)) / 86400000)) : null;
            const daysLabel = daysAgo === null ? "" : daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : `${daysAgo}d ago`;
            return (
              <div key={j.id || i} className="cp-applied-card" style={{ borderLeftColor: cfg.color, animationDelay: `${i * 60}ms` }}>
                <div className="cp-applied-card__logo" style={{ background: `${cfg.color}18` }}>
                  {logoSrc
                    ? <img src={logoSrc} alt={co} style={{ width:"100%", height:"100%", objectFit:"contain", borderRadius:8 }}/>
                    : <span style={{ color: cfg.color }}>{co[0]?.toUpperCase() || "J"}</span>}
                </div>
                <div className="cp-applied-card__info">
                  <div className="cp-applied-card__title">{j.title || "—"}</div>
                  <div className="cp-applied-card__company">{co}</div>
                  <div className="cp-applied-card__meta">
                    {j.location && <span>📍 {j.location}</span>}
                    {j.type && <span>💼 {j.type}</span>}
                    {daysLabel && <span>🕑 {daysLabel}</span>}
                  </div>
                  {j.recruiter_note && <div className="cp-applied-card__note" style={{ background: cfg.bg }}>💬 {j.recruiter_note}</div>}
                </div>
                <div className="cp-applied-card__right">
                  <span className="cp-applied-card__badge" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SAVED ── */}
      {subTab === "saved" && (
        <div className="cp-jobs__list">
          {sLoad && <div className="cp-jobs__loading"><span className="cp-spin cp-spin--lg" /><p>Loading saved jobs…</p></div>}
          {!sLoad && sError && <div className="cp-jobs__error">⚠ {sError}</div>}
          {!sLoad && !sError && saved.length === 0 && (
            <div className="cp-jobs__empty">
              <div className="cp-jobs__empty-icon">🔖</div>
              <h4>No saved jobs</h4>
              <p>Bookmark jobs from the jobs page to see them here</p>
              <a href="/employee/jobs" className="cp-btn cp-btn--primary">Browse jobs →</a>
            </div>
          )}
          {!sLoad && !sError && saved.map((j, i) => {
            const jid = j.job_id || j.id;
            return (
              <SavedJob
                key={jid || i}
                job={j}
                onRemove={removeSaved}
                onApply={handleApplyClick}
                isApplied={appliedSet.has(jid)}
                isApplying={applyingId === jid}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
const SECTION_TABS = [
  { id:"personal",  icon:"🧑", label:"Personal"   },
  { id:"kyc",       icon:"🔐", label:"KYC"         },
  { id:"education", icon:"🎓", label:"Education"   },
  { id:"skills",    icon:"⚔",  label:"Skills"      },
  { id:"resume",    icon:"📄", label:"Resume"      },
  { id:"projects",  icon:"🧩", label:"Projects"    },
  { id:"recommendations", icon:"💬", label:"Recommendations" },
  { id:"extra",     icon:"📚", label:"Extra"       },
  { id:"overview",  icon:"📊", label:"Overview"    },
];

const COUNTRIES = ["India","United States","United Kingdom","Canada","Australia","Germany","Singapore","UAE","Other"];

export default function CandidateProfile() {
  const [profile,       setProfile]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [activeTab,     setActiveTab]     = useState("profile");
  const [sectionTab,    setSectionTab]    = useState("personal");
  const [updateModal,   setUpdateModal]   = useState(null);
  const [usernameModal, setUsernameModal] = useState(false);
  const [toast,         setToast]         = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [complaintSummary, setComplaintSummary] = useState({ total: 0, visible: 0, open: 0, resolved: 0, hidden: 0 });
  const [complaintLoading, setComplaintLoading] = useState(false);
  // Rewards state
  const [earnedActions, setEarnedActions] = useState([]);
  const [totalPoints,   setTotalPoints]   = useState(0);

  const fileRef   = useRef();
  const avatarRef = useRef();

  useEffect(()=>{ if(!getToken()) window.location.href="/employee/login"; },[]);
  useEffect(()=>{ load(); },[]);// eslint-disable-line

  // Load rewards from backend or localStorage
  useEffect(()=>{
    try {
      const saved=localStorage.getItem("cp_earned_actions");
      if (saved){ const arr=JSON.parse(saved); setEarnedActions(arr); setTotalPoints(arr.reduce((s,k)=>s+(REWARD_ACTIONS[k]?.points||0),0)); }
    } catch {}
  },[]);

  async function load() {
    setLoading(true);
    try {
      const { data }=await axios.get(`${API}/candidate/profile`,{headers:authHeaders()});
      data.skills    = tryParse(data.skills,[]);
      data.locations = tryParse(data.locations,[]);
      data.courses   = tryParse(data.courses,[]);
      data.projects  = tryParse(data.projects,[]);
      data.education = tryParse(data.education,[]);
      if (!data.applied_jobs){
        try {
          const { data:ad }=await axios.get(`${API}/applications/mine`,{headers:authHeaders()});
          data.applied_jobs=ad.success?(ad.data||[]):[];
        } catch { data.applied_jobs=[]; }
      }
      setProfile(data);
      persistUser({...getUser(),...data});
      loadRecommendations(data.id);
      await loadComplaints(data.id);

      // Auto-detect earned rewards from existing profile
      autoDetectRewards(data);
    } catch {}
    finally { setLoading(false); }
  }

  async function loadComplaints(candidateId) {
    if (!candidateId) return;
    setComplaintLoading(true);
    try {
      const { data } = await axios.get(`${API}/candidate/${candidateId}/complaints`, { headers: authHeaders() });
      setComplaints(Array.isArray(data.complaints) ? data.complaints : []);
      setComplaintSummary(data.summary || { total: 0, visible: 0, open: 0, resolved: 0, hidden: 0 });
    } catch {
      setComplaints([]);
      setComplaintSummary({ total: 0, visible: 0, open: 0, resolved: 0, hidden: 0 });
    } finally {
      setComplaintLoading(false);
    }
  }

  function autoDetectRewards(data) {
    const auto=[];
    if (data?.mobile)       auto.push("phone_verified");
    if (data?.email)        auto.push("email_verified");
    if (data?.profile_photo)auto.push("photo_uploaded");
    if (data?.aadhar)       auto.push("aadhar_uploaded");
    if (data?.pan)          auto.push("pan_uploaded");
    if (data?.aadhar_photo) { if (!auto.includes("aadhar_uploaded")) auto.push("aadhar_uploaded"); }
    if (data?.pan_photo)    { if (!auto.includes("pan_uploaded"))    auto.push("pan_uploaded"); }
    if (data?.resume_url)   auto.push("cv_uploaded");
    setEarnedActions(auto);
    const pts=auto.reduce((s,k)=>s+(REWARD_ACTIONS[k]?.points||0),0);
    setTotalPoints(pts);
    localStorage.setItem("cp_earned_actions",JSON.stringify(auto));
  }

  function awardPoints(actionKey) {
    if (earnedActions.includes(actionKey)) return;
    const action=REWARD_ACTIONS[actionKey]; if(!action) return;
    const next=[...earnedActions,actionKey];
    setEarnedActions(next);
    const newTotal=next.reduce((s,k)=>s+(REWARD_ACTIONS[k]?.points||0),0);
    setTotalPoints(newTotal);
    localStorage.setItem("cp_earned_actions",JSON.stringify(next));
    showToast(`🎉 +${action.points} pts — ${action.label}!`,"success");
  }

  const loadRecommendations = useCallback(async(candidateId) => {
    if (!candidateId) return;
    try {
      const { data } = await axios.get(`${API}/candidate/${candidateId}/recommendations`, { headers: authHeaders() });
      setRecommendations(Array.isArray(data?.recommendations) ? data.recommendations : []);
    } catch {
      setRecommendations([]);
    }
  }, []);

  const approveRecommendation = useCallback(async(recommendationId) => {
    try {
      const { data } = await axios.patch(`${API}/candidate/recommendations/${recommendationId}/approve`, {}, { headers: authHeaders() });
      if (data?.success) {
        setRecommendations((prev) => prev.map((item) => (
          Number(item.id) === Number(recommendationId)
            ? { ...item, status: "approved", approved_at: new Date().toISOString(), can_approve: false }
            : item
        )));
        showToast("Recommendation approved.");
      }
    } catch (err) {
      showToast(err?.response?.data?.error || "Failed to approve recommendation", "error");
    }
  }, []);

  function showToast(msg,type="success"){ setToast({msg,type}); setTimeout(()=>setToast(null),4000); }

  const publicProfileUrl = profile?.username ? getPublicProfileUrl(profile.username) : "";

  const copyPublicProfileLink = async () => {
    if (!publicProfileUrl) return;
    try {
      await navigator.clipboard.writeText(publicProfileUrl);
      showToast("Public profile link copied!");
    } catch {
      window.prompt("Copy your public profile link", publicProfileUrl);
    }
  };

  const save=useCallback(async(field,value)=>{
    let sendValue=value;
    if (field==="aadhar"){
      sendValue=String(value||"").replace(/\D/g,"");
      if (sendValue.length>0&&sendValue.length!==12) throw new Error("Aadhaar must be 12 digits");
    }
    if (field==="pan"){
      sendValue=String(value||"").trim().toUpperCase();
      if (sendValue.length>0&&!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(sendValue)) throw new Error("PAN format: ABCDE1234F");
    }
    await axios.put(`${API}/candidate/profile`,{[field]:sendValue},{headers:authHeaders()});
    setProfile(p=>{ const u={...p,[field]:sendValue}; persistUser({...getUser(),...u}); return u; });

    // Award points for KYC fields
    if (field==="aadhar"&&sendValue) awardPoints("aadhar_uploaded");
    if (field==="pan"&&sendValue)    awardPoints("pan_uploaded");
  },[earnedActions]); // eslint-disable-line

  const saveEducation=async(eduList)=>{
    await axios.put(`${API}/candidate/profile`,{education:JSON.stringify(eduList)},{headers:authHeaders()});
    setProfile(p=>{ const u={...p,education:eduList}; persistUser({...getUser(),...u}); return u; });
  };

  const saveProjects=async(projectList)=>{
    await axios.put(`${API}/candidate/profile`,{projects:JSON.stringify(projectList)},{headers:authHeaders()});
    setProfile(p=>{ const u={...p,projects:projectList}; persistUser({...getUser(),...u}); return u; });
  };

  const saveTagField=useCallback(async(field,list)=>{
    const val=JSON.stringify(list);
    await axios.put(`${API}/candidate/profile`,{[field]:val},{headers:authHeaders()});
    setProfile(p=>{ const u={...p,[field]:list}; persistUser({...getUser(),...u}); return u; });
  },[]);

  async function handleSensitiveUpdate(type,value) {
    const field=type==="email"?"email":"mobile";
    await axios.put(`${API}/candidate/profile`,{[field]:value},{headers:authHeaders()});
    setProfile(p=>{ const u={...p,[field]:value}; persistUser({...getUser(),...u}); return u; });
    if (field==="mobile") awardPoints("phone_verified");
    if (field==="email")  awardPoints("email_verified");
  }

  function handleUsernameSaved(newUsername) {
    setProfile(p=>{ const u={...p,username:newUsername}; persistUser({...getUser(),...u}); return u; });
  }

  async function uploadResume(e) {
    const file=e.target.files[0]; if(!file) return;
    if (file.size>5*1024*1024){ showToast("Max file size is 5MB","error"); return; }
    const fd=new FormData(); fd.append("resume",file);
    try {
      const {data}=await axios.post(`${API}/candidate/upload-resume`,fd,{headers:{...authHeaders(),"Content-Type":"multipart/form-data"}});
      setProfile(p=>{ const u={...p,resume_url:data.resume_url}; persistUser({...getUser(),resume_url:data.resume_url}); return u; });
      awardPoints("cv_uploaded");
      showToast("✅ Resume uploaded! +20 pts earned");
    } catch(err) { showToast(err?.response?.data?.error||"Upload failed","error"); }
  }

  async function uploadAvatar(e) {
    const file=e.target.files[0]; if(!file) return;
    if (file.size>5*1024*1024){ showToast("Max 5MB","error"); return; }
    setProfile(p=>({...p,_localAvatar:URL.createObjectURL(file)}));
    const fd=new FormData(); fd.append("photo",file);
    try {
      const {data}=await axios.post(`${API}/candidate/upload-photo`,fd,{headers:{...authHeaders(),"Content-Type":"multipart/form-data"}});
      if (!data.success) throw new Error(data.error||"Upload failed");
      const photoUrl=data.photo_url||data.url;
      setProfile(p=>{ const u={...p,profile_photo:photoUrl,_localAvatar:null}; persistUser({...getUser(),...u,profile_photo:photoUrl}); return u; });
      awardPoints("photo_uploaded");
    } catch { setProfile(p=>({...p,_localAvatar:null})); }
  }

  function handleKYCPhotoUploaded(field) {
    if (field==="aadhar_photo") awardPoints("aadhar_uploaded");
    if (field==="pan_photo")    awardPoints("pan_uploaded");
  }

  function logout() {
    ["th_user","th_token","user","token"].forEach(k=>localStorage.removeItem(k));
    window.location.href="/";
  }

  const comp = getCompletion(profile);
  const lv   = getLevelInfo(totalPoints);

  const xpPct = useMemo(()=>{
    const next=lv.next; const prev=MILESTONES.find(m=>totalPoints>=m.pts);
    const from=prev?.pts||0;
    return next?Math.min(100,Math.round(((totalPoints-from)/(next-from))*100)):100;
  },[totalPoints,lv.next]);

  const fullName  = [profile?.first_name,profile?.last_name].filter(Boolean).join(" ")||"Your Name";
  const initials  = `${profile?.first_name?.[0]||""}${profile?.last_name?.[0]||""}`.toUpperCase()||"?";
  const photoSrc  = profile?._localAvatar||resolvePhoto(profile?.profile_photo)||null;
  const complaintCount = Number(complaintSummary?.total || complaints.length || 0);

  const sectionStatus = useMemo(()=>({
    personal:  !!(profile?.first_name&&profile?.last_name&&profile?.headline&&profile?.gender),
    kyc:       !!(profile?.aadhar&&profile?.pan),
    education: !!(profile?.education?.[0]?.degree),
    skills:    (profile?.skills?.length||0)>=3,
    resume:    !!profile?.resume_url,
    projects:  (profile?.projects?.length||0)>0,
    recommendations: recommendations.length > 0,
    complaints: complaintCount > 0,
    extra:     (profile?.courses?.length||0)>0,
    overview:  comp.pct>=50,
  }),[profile,comp.pct,recommendations,complaintCount]);

  const tabMap = { first_name:"personal",last_name:"personal",mobile:"personal",headline:"personal",gender:"personal",work_status:"personal",salary:"personal",country_iso:"personal",aadhar:"kyc",pan:"kyc",aadhar_photo:"kyc",pan_photo:"kyc",education:"education",skills:"skills",locations:"skills",resume_url:"resume",projects:"projects",recommendations:"recommendations",complaints:"complaints",courses:"extra" };

  if (loading) return (
    <div className="cp-page-loading">
      <span className="cp-spin cp-spin--lg"/>
      <p>Loading your profile…</p>
    </div>
  );

  return (
    <div className="cp-root" style={{ marginTop:90 }}>
      {toast&&<Toast msg={toast.msg} type={toast.type} onDone={()=>setToast(null)}/>}
      {updateModal&&<UpdateModal type={updateModal} current={updateModal==="email"?profile?.email:profile?.mobile} onClose={()=>setUpdateModal(null)} onSave={handleSensitiveUpdate}/>}
      {usernameModal&&<UsernameModal currentUsername={profile?.username} firstName={profile?.first_name} lastName={profile?.last_name} onClose={()=>setUsernameModal(false)} onSave={handleUsernameSaved}/>}

      {/* ── SIDEBAR ── */}
      <aside className="cp-aside">
        {/* Avatar */}
        <div onClick={()=>avatarRef.current?.click()} className="cp-avatar-wrap">
          {photoSrc ? <img src={photoSrc} alt="avatar" className="cp-avatar-img"/> : <div className="cp-avatar-initials">{initials}</div>}
          <div className="cp-avatar-overlay">📷</div>
          <div className="cp-avatar-level">Lv{["Rookie","Starter","Explorer","Achiever","Performer","Champion"].indexOf(lv.title)+1}</div>
        </div>
        <input ref={avatarRef} type="file" accept="image/*" style={{display:"none"}} onChange={uploadAvatar}/>

        <div className="cp-aside__name">{fullName}</div>
        <button className={`cp-aside__username-btn${!profile?.username?" cp-aside__username-btn--empty":""}`} onClick={()=>setUsernameModal(true)}>
          {profile?.username?`@${profile.username}`:"+ Set username"}
          {profile?.username&&<span className="cp-aside__username-edit">✎</span>}
        </button>

        {/* XP bar */}
        <div className="cp-aside__xp">
          <div className="cp-aside__xp-header">
            <span className="cp-aside__xp-level">{lv.emoji} {lv.title}</span>
            <span className="cp-aside__xp-score">{totalPoints} pts</span>
          </div>
          <div className="cp-aside__xp-bar"><div className="cp-aside__xp-fill" style={{width:`${xpPct}%`}}/></div>
          <div className="cp-aside__xp-sub">{lv.next-totalPoints} pts to next level</div>
        </div>

        {/* Profile completion */}
        <div className="cp-aside__comp">
          <div className="cp-aside__comp-row"><span>Profile</span><span className="cp-aside__comp-val">{comp.pct}%</span></div>
          <div className="cp-aside__comp-bar"><div className="cp-aside__comp-fill" style={{width:`${comp.pct}%`}}/></div>
          <div className="cp-aside__comp-sub">{comp.done}/{comp.items.length} sections done</div>
        </div>

        {/* Ready status */}
        <div className={`cp-aside__ready${comp.canApply?" cp-aside__ready--ok":" cp-aside__ready--warn"}`}>
          <div className="cp-aside__ready-title">{comp.canApply?"✅ Ready to apply":` ⚠ ${comp.reqTotal-comp.reqDone} required missing`}</div>
          <div className="cp-aside__ready-sub">{comp.canApply?"All required fields complete":"Finish required fields to apply"}</div>
        </div>

        <nav className="cp-aside__nav">
          {[
            { id:"profile",  icon:"👤", label:"My profile"     },
            { id:"jobs",     icon:"💼", label:"Jobs",    count:profile?.applied_jobs?.length||0 },
            { id:"recommendations", icon:"💬", label:"Recommendations", count:recommendations.filter((r) => String(r.status || "").toLowerCase() === "pending").length || null },
            { id:"complaints", icon:"🛡", label:"Tickets", count:complaintCount || null },
            { id:"rewards",  icon:"🏆", label:"Rewards", count:totalPoints>0?totalPoints:null   },
          ].map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)} className={`cp-nav-item${activeTab===t.id?" active":""}`}>
              <span style={{fontSize:16}}>{t.icon}</span>
              <span>{t.label}</span>
              {(t.count||0)>0&&<span className="cp-nav-badge">{t.count}</span>}
            </button>
          ))}
        </nav>

        <a href="/employee/jobs" className="cp-aside__browse">💼 Browse jobs</a>
        <button onClick={logout} className="cp-aside__logout">🚪 Log out</button>
      </aside>

      {/* ── MAIN ── */}
      <main className="cp-main">
        {/* Hero */}
        <div className="cp-hero">
          <div className="cp-hero__content">
            <div className="cp-hero__info">
              <h1 className="cp-hero__name">
                {fullName}
                {profile?.work_status&&<span className="cp-hero__status-badge">{profile.work_status==="fresher"?"🛡 Fresher":"⚔ Experienced"}</span>}
              </h1>
              {profile?.username&&(
                <div className="cp-hero__username">
                  <span>@{profile.username}</span>
                  <button onClick={()=>setUsernameModal(true)} className="cp-hero__username-change">Change</button>
                </div>
              )}
              {publicProfileUrl && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                  <a href={publicProfileUrl} target="_blank" rel="noreferrer" className="cp-btn cp-btn--ghost cp-btn--sm">
                    View public page
                  </a>
                  <button type="button" onClick={copyPublicProfileLink} className="cp-btn cp-btn--primary cp-btn--sm">
                    Copy share link
                  </button>
                </div>
              )}
              <p className="cp-hero__headline">{profile?.headline||"Add your professional headline to attract recruiters"}</p>
              <div className="cp-hero__meta">
                {profile?.email&&<span>✉ {profile.email}</span>}
                {profile?.mobile&&<span>📱 {profile.mobile}</span>}
                {profile?.country_iso&&<span>🌐 {profile.country_iso}</span>}
              </div>
              {totalPoints>0&&(
                <div className="cp-hero__pts-badge">
                  {lv.emoji} {lv.title} · {totalPoints} pts earned
                </div>
              )}
            </div>
            <div className="cp-hero__ring">
              <Ring pct={comp.pct} size={110} stroke={9}/>
              <div className="cp-hero__ring-center">
                <span className="cp-hero__ring-pct">{comp.pct}%</span>
                <span className="cp-hero__ring-lbl">complete</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="cp-tabbar">
          {[
            { id:"profile", label:"👤 Profile"  },
            { id:"jobs",    label:"💼 Jobs",    count:profile?.applied_jobs?.length||0 },
            { id:"rewards", label:"🏆 Rewards"  },
            { id:"complaints", label:"🛡 Tickets", count:complaintCount || null },
          ].map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)} className={`cp-tabbar__btn${activeTab===t.id?" active":""}`}>
              {t.label}{(t.count||0)>0&&<span className="cp-tabbar__count">{t.count}</span>}
            </button>
          ))}
        </div>

        <div className="cp-content">

          {/* ── PROFILE TAB ── */}
          {activeTab==="profile"&&(
            <div className="cp-profile-view">
              {/* Section sub-tabs */}
              <div className="cp-section-tabs">
                {SECTION_TABS.map(st=>(
                  <button key={st.id} onClick={()=>setSectionTab(st.id)} className={`cp-section-tab${sectionTab===st.id?" cp-section-tab--active":""}`}>
                    <span className="cp-section-tab__icon">{st.icon}</span>
                    <span className="cp-section-tab__label">{st.label}</span>
                    <span className={`cp-section-tab__dot${sectionStatus[st.id]?" cp-section-tab__dot--done":""}`}/>
                  </button>
                ))}
              </div>

              {/* ── PERSONAL ── */}
              {sectionTab==="personal"&&(
                <div className="cp-section-body">
                  <div className="cp-section-header">
                    <h2 className="cp-section-title">🧑 Personal information</h2>
                    <p className="cp-section-desc">Your basic identity info. Fields marked <span className="cp-req-star">*</span> are required to apply for jobs.</p>
                  </div>
                  <div className="cp-form-grid">
                    <SmartField label="First name" fieldKey="first_name" value={profile?.first_name} placeholder="Your first name" required onSave={v=>save("first_name",v)}/>
                    <SmartField label="Last name"  fieldKey="last_name"  value={profile?.last_name}  placeholder="Your last name"  required onSave={v=>save("last_name",v)}/>

                    {/* Username */}
                    <div className="cp-field">
                      <label className="cp-label">Username</label>
                      <div className="cp-username-field" onClick={()=>setUsernameModal(true)}>
                        <span className="cp-username-field__at">@</span>
                        <span className="cp-username-field__val">{profile?.username||<em style={{color:"var(--cp-ink-5)"}}>Set a username</em>}</span>
                        <span className="cp-username-field__action">Change</span>
                      </div>
                      <p className="cp-field__note">Can be changed once every 3 months</p>
                    </div>

                    {/* Email */}
                    <div className="cp-field">
                      <div className="cp-field__header">
                        <label className="cp-label">Email <span className="cp-req-star">*</span></label>
                        <span className="cp-pts-hint">+10 pts on verify</span>
                      </div>
                      <div className="cp-sensitive-field" onClick={()=>setUpdateModal("email")}>
                        <span style={{flex:1,color:profile?.email?"var(--cp-ink)":"var(--cp-ink-5)",fontStyle:profile?.email?"normal":"italic"}}>{profile?.email||"Add email address"}</span>
                        <span className="cp-update-chip">Update</span>
                      </div>
                    </div>

                    {/* Mobile */}
                    <div className="cp-field">
                      <div className="cp-field__header">
                        <label className="cp-label">Mobile <span className="cp-req-star">*</span></label>
                        <span className="cp-pts-hint">+10 pts on verify</span>
                      </div>
                      <div className="cp-sensitive-field" onClick={()=>setUpdateModal("mobile")}>
                        <span style={{flex:1,color:profile?.mobile?"var(--cp-ink)":"var(--cp-ink-5)",fontStyle:profile?.mobile?"normal":"italic"}}>{profile?.mobile||"Add mobile number"}</span>
                        <span className="cp-update-chip">Update</span>
                      </div>
                    </div>

                    <SmartSelect label="Gender" fieldKey="gender" value={profile?.gender} required options={["Male","Female","Non-binary","Prefer not to say"]} placeholder="Select gender" onSave={v=>save("gender",v)}/>
                    <SmartSelect label="Country" fieldKey="country_iso" value={profile?.country_iso} required options={COUNTRIES.map(c=>({value:c,label:c}))} placeholder="Select country" onSave={v=>save("country_iso",v)}/>
                    <SmartSelect label="Work status" fieldKey="work_status" value={profile?.work_status} options={[{value:"fresher",label:"🛡 Fresher"},{value:"experienced",label:"⚔ Experienced"}]} placeholder="Select status" onSave={v=>save("work_status",v)}/>
                    <SmartField label="Expected salary (₹/year)" fieldKey="salary" value={profile?.salary} placeholder="e.g. 6,00,000 or 8 LPA" onSave={v=>save("salary",v)}/>

                    <div className="cp-field cp-field--full">
                      <SmartField label="Professional headline" fieldKey="headline" value={profile?.headline} required multiline placeholder='"Full Stack Developer | React & Node.js | 3 Years Experience"' onSave={v=>save("headline",v)} note="Appears on your profile card and helps recruiters find you."/>
                    </div>
                  </div>

                  {(!profile?.aadhar||!profile?.pan||!profile?.aadhar_photo||!profile?.pan_photo)&&(
                    <div className="cp-kyc-warn">
                      ⚠ <strong>KYC incomplete:</strong> {[!profile?.aadhar&&"Aadhaar number",!profile?.aadhar_photo&&"Aadhaar photo",!profile?.pan&&"PAN number",!profile?.pan_photo&&"PAN photo"].filter(Boolean).join(", ")} still needed.
                      <button className="cp-btn cp-btn--ghost cp-btn--sm" onClick={()=>setSectionTab("kyc")} style={{marginLeft:8}}>Go to KYC →</button>
                    </div>
                  )}

                  <div className="cp-section-nav">
                    <button onClick={()=>setSectionTab("kyc")} className="cp-btn cp-btn--primary">Next: KYC →</button>
                  </div>
                </div>
              )}

              {/* ── KYC ── */}
              {sectionTab==="kyc"&&(
                <div className="cp-section-body">
                  <div className="cp-section-header">
                    <h2 className="cp-section-title">🔐 KYC documents</h2>
                    <p className="cp-section-desc">Required to apply for jobs. All data is encrypted and secure. Earn <strong>+30 pts</strong> per document verified.</p>
                  </div>

                  <div className="cp-kyc-security-note">
                    🔒 Your documents are end-to-end encrypted and only shared with employers upon confirmed hiring — never before.
                  </div>

                  {/* KYC progress */}
                  {(()=>{
                    const kycItems=[
                      {label:"Aadhaar number",done:!!profile?.aadhar},
                      {label:"Aadhaar photo", done:!!profile?.aadhar_photo},
                      {label:"PAN number",    done:!!profile?.pan},
                      {label:"PAN photo",     done:!!profile?.pan_photo},
                    ];
                    const doneCnt=kycItems.filter(i=>i.done).length;
                    const allDone=doneCnt===kycItems.length;
                    return (
                      <div className={`cp-kyc-progress${allDone?" cp-kyc-progress--done":""}`}>
                        <div className="cp-kyc-progress__label">
                          {allDone?"✅ KYC complete — verified!":`⏳ ${doneCnt}/4 documents completed`}
                        </div>
                        <div className="cp-aside__xp-bar"><div className="cp-aside__xp-fill" style={{width:`${(doneCnt/4)*100}%`}}/></div>
                        <div className="cp-kyc-progress__chips">
                          {kycItems.map(item=>(
                            <span key={item.label} className={`cp-kyc-chip${item.done?" cp-kyc-chip--done":" cp-kyc-chip--pending"}`}>
                              {item.done?"✓":""} {item.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Aadhaar */}
                  <div className="cp-doc-card">
                    <div className="cp-doc-card__head">
                      <div className="cp-doc-card__icon" style={{background:"#eff6ff"}}>🪪</div>
                      <div>
                        <div className="cp-doc-card__title">Aadhaar card</div>
                        <div className="cp-doc-card__sub">Government-issued 12-digit identity</div>
                      </div>
                      {profile?.aadhar&&profile?.aadhar_photo&&<span className="cp-doc-verified">✓ Verified</span>}
                      <span className="cp-pts-hint" style={{marginLeft:"auto"}}>+30 pts each</span>
                    </div>
                    <div className="cp-doc-card__body">
                      <SmartField label="Aadhaar number" fieldKey="aadhar" value={profile?.aadhar} required masked placeholder="Enter 12-digit Aadhaar" inputType="tel" onSave={v=>save("aadhar",v)} note="12 digits only — displayed masked for security"/>
                      <div>
                        <label className="cp-label" style={{marginBottom:8,display:"block"}}>Aadhaar card photo <span className="cp-req-star">*</span></label>
                        <KYCPhotoUpload field="aadhar_photo" existing={profile?.aadhar_photo} required label="Aadhaar card"
                          onUploaded={url=>{ setProfile(p=>({...p,aadhar_photo:url})); handleKYCPhotoUploaded("aadhar_photo"); }}
                          onPointsEarned={handleKYCPhotoUploaded}/>
                      </div>
                    </div>
                  </div>

                  {/* PAN */}
                  <div className="cp-doc-card">
                    <div className="cp-doc-card__head">
                      <div className="cp-doc-card__icon" style={{background:"#fffbeb"}}>💳</div>
                      <div>
                        <div className="cp-doc-card__title">PAN card</div>
                        <div className="cp-doc-card__sub">Permanent account number for tax</div>
                      </div>
                      {profile?.pan&&profile?.pan_photo&&<span className="cp-doc-verified">✓ Verified</span>}
                      <span className="cp-pts-hint" style={{marginLeft:"auto"}}>+30 pts each</span>
                    </div>
                    <div className="cp-doc-card__body">
                      <SmartField label="PAN number" fieldKey="pan" value={profile?.pan} required masked placeholder="e.g. ABCDE1234F" onSave={v=>save("pan",v)} note="Format: 5 letters, 4 digits, 1 letter"/>
                      <div>
                        <label className="cp-label" style={{marginBottom:8,display:"block"}}>PAN card photo <span className="cp-req-star">*</span></label>
                        <KYCPhotoUpload field="pan_photo" existing={profile?.pan_photo} required label="PAN card"
                          onUploaded={url=>{ setProfile(p=>({...p,pan_photo:url})); handleKYCPhotoUploaded("pan_photo"); }}
                          onPointsEarned={handleKYCPhotoUploaded}/>
                      </div>
                    </div>
                  </div>

                  <div className="cp-section-nav">
                    <button onClick={()=>setSectionTab("personal")} className="cp-btn cp-btn--ghost">← Personal</button>
                    <button onClick={()=>setSectionTab("education")} className="cp-btn cp-btn--primary">Next: Education →</button>
                  </div>
                </div>
              )}

              {/* ── EDUCATION ── */}
              {sectionTab==="education"&&(
                <div className="cp-section-body">
                  <div className="cp-section-header">
                    <h2 className="cp-section-title">🎓 Education</h2>
                    <p className="cp-section-desc">Add your qualifications. At least one degree is required. <span className="cp-pts-hint">+200 pts for first degree</span></p>
                  </div>
                  <EducationEditor educations={profile?.education||[]} onSave={saveEducation}/>
                  <div className="cp-section-nav">
                    <button onClick={()=>setSectionTab("kyc")} className="cp-btn cp-btn--ghost">← KYC</button>
                    <button onClick={()=>setSectionTab("skills")} className="cp-btn cp-btn--primary">Next: Skills →</button>
                  </div>
                </div>
              )}

              {/* ── SKILLS ── */}
              {sectionTab==="skills"&&(
                <div className="cp-section-body">
                  <div className="cp-section-header">
                    <h2 className="cp-section-title">⚔ Skills & locations</h2>
                    <p className="cp-section-desc">Add at least 3 skills to unlock XP and improve job matching. <span className="cp-pts-hint">+150 pts for 3+ skills</span></p>
                  </div>
                  <div className="cp-skills-banner">
                    {(profile?.skills||[]).length>=3?"✅":"⭕"} {(profile?.skills||[]).length} skill{(profile?.skills||[]).length!==1?"s":""} added
                    {(profile?.skills||[]).length<3&&<span style={{color:"var(--cp-amber)",fontWeight:700}}> — add {3-(profile?.skills||[]).length} more for XP</span>}
                  </div>
                  <TagEditor tags={profile?.skills||[]} required label="Professional skills" placeholder="Type a skill (e.g. React, Python) and press Enter…" onSave={list=>saveTagField("skills",list)}/>
                  <div style={{marginTop:28}}>
                    <TagEditor tags={profile?.locations||[]} label="Preferred job locations" placeholder="Type a city and press Enter…" onSave={list=>saveTagField("locations",list)}/>
                  </div>
                  <div className="cp-section-nav">
                    <button onClick={()=>setSectionTab("education")} className="cp-btn cp-btn--ghost">← Education</button>
                    <button onClick={()=>setSectionTab("resume")} className="cp-btn cp-btn--primary">Next: Resume →</button>
                  </div>
                </div>
              )}

              {/* ── RESUME ── */}
              {sectionTab==="resume"&&(
                <div className="cp-section-body">
                  <div className="cp-section-header">
                    <h2 className="cp-section-title">📄 Resume / CV</h2>
                    <p className="cp-section-desc">Upload your resume to unlock ATS checking and AI tools. <span className="cp-pts-hint">+20 pts on upload</span></p>
                  </div>

                  {profile?.resume_url ? (
                    <div>
                      <div className="cp-resume-ok">
                        <span style={{fontSize:28}}>✅</span>
                        <div style={{flex:1}}>
                          <p style={{fontWeight:600,color:"var(--cp-green)",marginBottom:4}}>Resume uploaded successfully</p>
                          <p style={{fontSize:13,color:"var(--cp-ink-4)"}}>You've earned your +20 pts. Use the AI tools below to improve your resume.</p>
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <a href={`${process.env.REACT_APP_API_URL}${profile.resume_url}`} target="_blank" rel="noreferrer" className="cp-btn cp-btn--success">👁 View</a>
                          <button onClick={()=>fileRef.current?.click()} className="cp-btn cp-btn--ghost">🔄 Replace</button>
                        </div>
                      </div>
                      <div className="cp-resume-actions">
                        {[
                          { icon:"🎯", label:"ATS score checker",  sub:"See how recruiters rate your resume", href:`/employee/ats-score-checker?resume=${encodeURIComponent(profile.resume_url)}`, cls:"cp-resume-action-card--ats" },
                          { icon:"✨", label:"AI resume builder",   sub:"Create a polished resume with AI",    href:`/employee/resume-builder?resume=${encodeURIComponent(profile.resume_url)}`,  cls:"cp-resume-action-card--builder" },
                          { icon:"💼", label:"Salary guide",        sub:"Research salary ranges for your role",href:`/employee/salary-guide`,                                                     cls:"cp-resume-action-card--salary" },
                        ].map(a=>(
                          <a key={a.label} href={a.href} className={`cp-resume-action-card ${a.cls}`}>
                            <div className="cp-resume-action-icon">{a.icon}</div>
                            <div className="cp-resume-action-label">{a.label}</div>
                            <div className="cp-resume-action-sub">{a.sub}</div>
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="cp-resume-drop" onClick={()=>fileRef.current?.click()}>
                      <div style={{fontSize:44,marginBottom:12}}>📤</div>
                      <h4 style={{fontWeight:700,marginBottom:6,fontSize:17}}>Upload your resume</h4>
                      <p style={{fontSize:13,color:"var(--cp-ink-4)",marginBottom:16}}>PDF, DOC, DOCX · max 5MB · drag & drop or click</p>
                      <div className="cp-btn cp-btn--primary" style={{pointerEvents:"none"}}>Choose file</div>
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{display:"none"}} onChange={uploadResume}/>
                  <div className="cp-section-nav">
                    <button onClick={()=>setSectionTab("skills")} className="cp-btn cp-btn--ghost">← Skills</button>
                    <button onClick={()=>setSectionTab("projects")} className="cp-btn cp-btn--primary">Next: Projects →</button>
                  </div>
                </div>
              )}

              {/* ── EXTRA ── */}
              {/* ─── PROJECTS ─────────────────────────────────────────────── */}
              {sectionTab==="projects"&&(
                <div className="cp-section-body">
                  <div className="cp-section-header">
                    <h2 className="cp-section-title">🧩 Projects</h2>
                    <p className="cp-section-desc">Show your best work with project links and cover images. <span className="cp-pts-hint">+120 pts for any project</span></p>
                  </div>
                  <ProjectsEditor projects={profile?.projects||[]} onSave={saveProjects}/>
                  <div className="cp-section-nav">
                    <button onClick={()=>setSectionTab("resume")} className="cp-btn cp-btn--ghost">← Resume</button>
                    <button onClick={()=>setSectionTab("extra")} className="cp-btn cp-btn--primary">Next: Extra →</button>
                  </div>
                </div>
              )}

              {sectionTab==="extra"&&(
                <div className="cp-section-body">
                  <div className="cp-section-header">
                    <h2 className="cp-section-title">📚 Courses & certifications</h2>
                    <p className="cp-section-desc">Add certifications and online courses to boost your profile. <span className="cp-pts-hint">+60 pts for first course</span></p>
                  </div>
                  <TagEditor tags={profile?.courses||[]} label="Courses & certifications" placeholder="e.g. AWS Certified, React Bootcamp…" onSave={list=>saveTagField("courses",list)}/>
                  <div className="cp-section-nav">
                    <button onClick={()=>setSectionTab("projects")} className="cp-btn cp-btn--ghost">← Projects</button>
                    <button onClick={()=>setSectionTab("overview")} className="cp-btn cp-btn--primary">View overview →</button>
                  </div>
                </div>
              )}

              {/* ── OVERVIEW ── */}
              {sectionTab==="overview"&&(
                <div className="cp-section-body">
                  <div className="cp-section-header">
                    <h2 className="cp-section-title">📊 Profile overview</h2>
                    <p className="cp-section-desc">Your overall profile completion and readiness at a glance.</p>
                  </div>
                  <div className="cp-overview-grid">
                    <div className="cp-overview-ring-card">
                      <div style={{position:"relative",width:120,height:120,flexShrink:0}}>
                        <Ring pct={comp.pct} size={120} stroke={10}/>
                        <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center"}}>
                          <span style={{fontSize:24,fontWeight:800,color:"var(--cp-brand)",lineHeight:1}}>{comp.pct}%</span>
                          <div style={{fontSize:10,color:"var(--cp-ink-5)",fontWeight:600,marginTop:2}}>complete</div>
                        </div>
                      </div>
                      <div>
                        <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>{comp.done}/{comp.items.length} sections done</div>
                        <div className={`cp-aside__ready${comp.canApply?" cp-aside__ready--ok":" cp-aside__ready--warn"}`}>
                          <div className="cp-aside__ready-title">{comp.canApply?"✅ Ready to apply!":"⚠ Complete required fields"}</div>
                          <div className="cp-aside__ready-sub">{comp.canApply?"You're all set!":"Fill required fields to apply"}</div>
                        </div>
                      </div>
                    </div>
                    <div className="cp-comp-list">
                      {comp.items.map(item=>(
                        <div key={item.label} className={`cp-comp-item${item.done?" cp-comp-item--done":""}`}>
                          <span style={{fontSize:14}}>{item.done?"✅":"⭕"}</span>
                          <span style={{flex:1,fontWeight:item.required?600:400,fontSize:13}}>
                            {item.label}{item.required&&!item.done&&<span className="cp-req-star" style={{marginLeft:4,fontSize:9}}> req</span>}
                          </span>
                          <span style={{fontSize:12,color:item.done?"var(--cp-green)":"var(--cp-ink-5)",fontWeight:600}}>+{item.xp}xp</span>
                          {!item.done&&(
                            <button onClick={()=>setSectionTab(tabMap[item.key]||"personal")} className="cp-btn cp-btn--ghost cp-btn--sm" style={{fontSize:11}}>Fill →</button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── JOBS TAB ── */}
          {activeTab==="jobs"&&<JobsTab profileApplied={profile?.applied_jobs||[]}/>}

          {activeTab==="recommendations"&&(
            <div className="cp-reco-view">
              <div className="cp-section-header">
                <h2 className="cp-section-title">💬 Recommendation approvals</h2>
                <p className="cp-section-desc">Recruiters can send recommendations here. Approve the ones you want to publish on your public profile.</p>
              </div>

              <div className="cp-reco-stats">
                <div className="cp-reco-stat">
                  <span className="cp-reco-stat__label">Total received</span>
                  <strong>{recommendations.length}</strong>
                </div>
                <div className="cp-reco-stat">
                  <span className="cp-reco-stat__label">Pending approval</span>
                  <strong>{recommendations.filter((r) => String(r.status || "").toLowerCase() === "pending").length}</strong>
                </div>
                <div className="cp-reco-stat">
                  <span className="cp-reco-stat__label">Approved</span>
                  <strong>{recommendations.filter((r) => String(r.status || "").toLowerCase() === "approved").length}</strong>
                </div>
              </div>

              {recommendations.length===0 ? (
                <div className="cp-reco-empty">
                  <div className="cp-reco-empty__icon">💬</div>
                  <p>No recommendations yet.</p>
                  <span>Recruiter recommendations will appear here once they are added.</span>
                </div>
              ) : (
                <div className="cp-reco-list">
                  {recommendations.map((reco) => {
                    const status = String(reco.status || "").toLowerCase();
                    return (
                      <div key={reco.id} className={`cp-reco-card cp-reco-card--${status}`}>
                        <div className="cp-reco-card__head">
                          <div>
                            <div className="cp-reco-card__name">{reco.reviewer_name || reco.recommender_name || "Recruiter"}</div>
                            <div className="cp-reco-card__meta">
                              {reco.reviewer_position || reco.recommender_position || "Recruiter"}
                              {reco.reviewer_company || reco.recommender_company ? ` · ${reco.reviewer_company || reco.recommender_company}` : ""}
                            </div>
                          </div>
                          <span className={`cp-reco-status cp-reco-status--${status}`}>
                            {status === "approved" ? "Approved" : "Pending"}
                          </span>
                        </div>
                        <p className="cp-reco-card__comment">{reco.comment}</p>
                        <div className="cp-reco-card__foot">
                          <span>{reco.created_at ? new Date(reco.created_at).toLocaleDateString() : "Recently"}</span>
                          {reco.can_approve && (
                            <button className="cp-btn cp-btn--primary cp-btn--sm" onClick={() => approveRecommendation(reco.id)}>
                              Approve
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── REWARDS TAB ── */}
          {activeTab==="complaints"&&(
            <div className="cp-reco-view">
              <div className="cp-section-header">
                <h2 className="cp-section-title">🛡 Complaint tickets</h2>
                <p className="cp-section-desc">Complaint tickets from recruiters are shown here. Only open tickets appear on your profile.</p>
              </div>
              <div className="cp-reco-stats">
                <div className="cp-reco-stat"><span className="cp-reco-stat__label">Total tickets</span><strong>{complaintCount}</strong></div>
                <div className="cp-reco-stat"><span className="cp-reco-stat__label">Open</span><strong>{complaintSummary.open || 0}</strong></div>
                <div className="cp-reco-stat"><span className="cp-reco-stat__label">Resolved</span><strong>{complaintSummary.resolved || 0}</strong></div>
              </div>
              {complaintLoading ? (
                <div className="cp-reco-empty">
                  <div className="cp-spin cp-spin--lg" />
                  <p>Loading ticket details…</p>
                </div>
              ) : complaints.length === 0 ? (
                <div className="cp-reco-empty">
                  <div className="cp-reco-empty__icon">🛡</div>
                  <p>No open complaint tickets right now.</p>
                  <span>Resolved or hidden tickets do not appear on your profile.</span>
                </div>
              ) : (
                <div className="cp-reco-list">
                  {complaints.map((ticket) => (
                    <div key={ticket.id} className="cp-reco-card cp-reco-card--pending">
                      <div className="cp-reco-card__head">
                        <div>
                          <div className="cp-reco-card__name">{ticket.title}</div>
                          <div className="cp-reco-card__meta">{ticket.recruiter_company || ticket.recruiter_name || "Recruiter"}{ticket.created_at ? ` · ${new Date(ticket.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : ""}</div>
                        </div>
                        <span className="cp-reco-status cp-reco-status--pending">{String(ticket.status || "open").replace(/_/g, " ")}</span>
                      </div>
                      <p className="cp-reco-card__comment" style={{ whiteSpace: "pre-wrap" }}>{ticket.message || "No message provided."}</p>
                      <div className="cp-reco-card__foot">
                        <span style={{ textTransform: "capitalize" }}>{String(ticket.category || "misconduct").replace(/_/g, " ")}</span>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          {ticket.visible_on_profile ? <span className="cp-nav-badge" style={{ background: "#dcfce7", color: "#16a34a" }}>Visible</span> : <span className="cp-nav-badge" style={{ background: "#fee2e2", color: "#dc2626" }}>Hidden</span>}
                          {(ticket.proof_url || ticket.proof_path) && <a href={resolveApiFileUrl(ticket.proof_url || ticket.proof_path)} target="_blank" rel="noreferrer" className="cp-btn cp-btn--ghost cp-btn--sm">View proof</a>}
                        </div>
                      </div>
                      {(ticket.hidden_reason || ticket.admin_note) && (
                        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                          {ticket.hidden_reason && <div style={{ padding: 12, borderRadius: 12, background: "#f2f5ea", border: "1px solid #c8d4a0" }}><strong style={{ display: "block", marginBottom: 4 }}>Hidden reason</strong><span>{ticket.hidden_reason}</span></div>}
                          {ticket.admin_note && <div style={{ padding: 12, borderRadius: 12, background: "#eff6ff", border: "1px solid #bfdbfe" }}><strong style={{ display: "block", marginBottom: 4 }}>Admin note</strong><span>{ticket.admin_note}</span></div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab==="rewards"&&<RewardsPage profile={profile} earnedActions={earnedActions} totalPoints={totalPoints}/>}

        </div>
      </main>
    </div>
  );
}
